import './style.scss'

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Col, Form, Modal, Row, InputGroup } from 'react-bootstrap-v5'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import ar from 'date-fns/locale/ar-SA'
import en from 'date-fns/locale/en-US'
import { format } from 'date-fns'
import { MultiSelect } from 'react-multi-select-component'

import { busyTimeApi } from '../../redux/BusyTimeApi'
import { userApi } from '../../../users/redux/UserApi'
import { TimeSlotWorking, BusyTimeSPObject, DateBusyTimeData } from '../../models/BusyTime'
import { Team, UserModel } from '../../../users/models'
import { HTTP_RESPONSE_STATUS } from '../../../../../setup/constants/Http';
import { BusyStatus, BusyStatusType, Languages, popperPlacement, USER_TYPE } from 'app/utils/common/constants';
import { Placement } from '@popperjs/core';
import { ToastDefaultConfig } from '../../../../../setup/toast/ToastConfig';
import { selectLanguage } from '../../../auth';
import { useAppSelector } from 'setup/redux/Hooks'

interface Props {
  userId: string | null
  userRole: string // "200" (Bus/Tech) or "500" (Worker)
  onHide?: () => void
  onCloseButtonClick?: () => void
  onSuccess?: () => void
}

// Constants

// Helper to get default date range (current date)
const getDefaultDateRange = (): [Date | null, Date | null] => {
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day
  return [today, today]
}

export const SetBusyTimeModal: React.FC<Props> = ({ userId, userRole, onHide, onCloseButtonClick, onSuccess }) => {
  const isBus = userRole === USER_TYPE.TECHNICIAN
  const isWorker = userRole === USER_TYPE.WORKER
  const { t } = useTranslation()

  // Multiselect strings with translations
  const MULTISELECT_STRINGS = useMemo(() => ({
    selectSomeItems: isWorker
      ? t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.SELECT_WORKERS')
      : t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.SELECT_BUSES'),
    allItemsSelected: isWorker
      ? t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.ALL_WORKERS_SELECTED')
      : t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.ALL_BUSES_SELECTED'),
    selectAll: t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.SELECT_ALL'),
    search: isWorker
      ? t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.SEARCH_WORKERS')
      : t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.SEARCH_BUSES'),
    noOptions: isWorker
      ? t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.NO_WORKERS_AVAILABLE')
      : t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.NO_BUSES_AVAILABLE'),
    clear: t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.CLEAR_SELECTION'),
    allItemsAreSelected: t('USERS_PAGE.STEP_UPDATE_USER.MULTISELECT.ALL_ITEMS_SELECTED'),
  }), [t, isWorker])

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false)
  const [isLoadingBusyTimeDays, setIsLoadingBusyTimeDays] = useState(false)
  const [selectedBuses, setSelectedBuses] = useState<Team[]>([])
  const [selectedWorkers, setSelectedWorkers] = useState<UserModel[]>([])
  const [busyDateRange, setBusyDateRange] = useState<[Date | null, Date | null]>(getDefaultDateRange())
  const [blockAllDay, setBlockAllDay] = useState<boolean>(false)
  const [blockAllDayDisplay, setBlockAllDayDisplay] = useState<boolean>(false) // Display value only (not trigger updates)
  const [activeTab, setActiveTab] = useState<'day' | 'week'>('day')
  const [timeSlots, setTimeSlots] = useState<TimeSlotWorking[]>([])
  const [busOptions, setBusOptions] = useState<Team[]>([])
  const [workerOptions, setWorkerOptions] = useState<UserModel[]>([])
  const [busyTimeDays, setBusyTimeDays] = useState<number | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [blockAllWeekDisplay, setBlockAllWeekDisplay] = useState<boolean>(false) // Display value only (not trigger updates)
  const [weekDayStatuses, setWeekDayStatuses] = useState<{ [key: string]: BusyStatusType }>({})
  const [hasEmptyTimeSlots, setHasEmptyTimeSlots] = useState<boolean>(false)
  const [isRepeated, setIsRepeated] = useState<boolean>(false)
  const language = useAppSelector(selectLanguage);

  // Store busy time data for each selected date (timeSlots + blockAllDay)
  const [busyTimeByDate, setBusyTimeByDate] = useState<Map<string, DateBusyTimeData>>(new Map())
  const busyTimeByDateRef = useRef<Map<string, DateBusyTimeData>>(new Map())

  // Store repeated setting for each selected date
  const [repeatedByDate, setRepeatedByDate] = useState<Map<string, boolean>>(new Map())
  // Store date range for each repeated date (to know which range was used when repeated was enabled)
  const [repeatedRangeByDate, setRepeatedRangeByDate] = useState<Map<string, [Date, Date]>>(new Map())

  // Temporary state for date picker (only applied when Done is clicked)
  const [tempDateRange, setTempDateRange] = useState<[Date | null, Date | null]>([null, null])
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  // Ref to control date picker popup
  const datePickerRef = useRef<any>(null)

  // State to track if initial busy time data has been loaded
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false)

  // Store selected time slots for each date
  const [selectedTimeSlotsByDate, setSelectedTimeSlotsByDate] = useState<Map<string, TimeSlotWorking[]>>(new Map())

  // Track dates that have been modified by user in current session (to avoid sending all dates from server)
  const [modifiedDatesInSession, setModifiedDatesInSession] = useState<Set<string>>(new Set())

  // Flags removed - now handling toggle changes directly in onChange

  // Sync busyTimeByDateRef with busyTimeByDate state
  useEffect(() => {
    busyTimeByDateRef.current = busyTimeByDate
  }, [busyTimeByDate])

  // Initialize tempDateRange with current busyDateRange
  useEffect(() => {
    setTempDateRange(busyDateRange)
  }, [busyDateRange])

  // Extract selected date range for dependency tracking
  const selectedDate = useMemo(() => busyDateRange, [busyDateRange])

  const rtlMode = language === Languages.ar;

  // Helper function to create consistent dateKey
  const createDateKey = useCallback((date: Date): string => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${year}-${month}-${day}`
  }, [])

  // Compute status from slots
  const computeStatusFromSlots = useCallback((slots: TimeSlotWorking[]): BusyStatusType => {
    const allBusy = slots.every(slot => slot.isBusyTime)
    const allOff = slots.every(slot => !slot.isAvailable && !slot.isBusyTime)
    const allAvailable = slots.every(slot => slot.isAvailable && !slot.isBusyTime)
    const hasBusy = slots.some(slot => slot.isBusyTime)
    const hasAvailable = slots.some(slot => slot.isAvailable && !slot.isBusyTime)
    if (allBusy) return BusyStatus.BUSY
    if (allOff) return BusyStatus.OFF
    if (allAvailable) return BusyStatus.AVAILABLE
    if (hasBusy && hasAvailable) return BusyStatus.PARTIALLY_BUSY
    return BusyStatus.AVAILABLE
  }, [])

  // Helper function to check if time slots are available for a specific date
  const hasTimeSlotsForDate = useCallback((date: Date): boolean => {
    const dateKey = createDateKey(date)
    const savedData = busyTimeByDateRef.current.get(dateKey)
    return Boolean(savedData && savedData.timeSlots && savedData.timeSlots.length > 0)
  }, [createDateKey])

  // Recompute Block All Week toggle based on cached statuses within current/next range
  // This only updates display, does NOT trigger data changes
  const recomputeBlockAllWeek = useCallback((start: Date | null, end: Date | null) => {
    if (!start || !end) {
      setBlockAllWeekDisplay(false)
      return
    }

    const startDate = new Date(start)
    const endDate = new Date(end)
    const cursor = new Date(startDate)

    let hasAnyDayWithSlots = false
    let allBusy = true

    while (cursor <= endDate) {
      const key = createDateKey(cursor)
      const cached = busyTimeByDateRef.current.get(key)
      if (cached && cached.timeSlots && cached.timeSlots.length > 0) {
        hasAnyDayWithSlots = true
        const status = computeStatusFromSlots(cached.timeSlots)
        if (status !== BusyStatus.BUSY) {
          allBusy = false
          break
        }
      }
      cursor.setDate(cursor.getDate() + 1)
    }

    setBlockAllWeekDisplay(hasAnyDayWithSlots && allBusy)
  }, [createDateKey, computeStatusFromSlots])

  // Helper function to check if a date has busy time (read from reactive state so month change updates immediately)
  const hasBusyTimeForDate = useCallback((date: Date): boolean => {
    const dateKey = createDateKey(date)
    const savedData = busyTimeByDate.get(dateKey)
    if (savedData && savedData.timeSlots && savedData.timeSlots.length > 0) {
      return savedData.timeSlots.some((slot: TimeSlotWorking) => slot.isBusyTime)
    }
    return false
  }, [createDateKey, busyTimeByDate])

  // Helper function to get day class name for calendar
  const getDayClassName = useCallback((date: Date): string => {
    const classes: string[] = []
    
    // Check if date is in the past
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const dateToCheck = new Date(date)
    dateToCheck.setHours(0, 0, 0, 0)
    const isPastDate = dateToCheck < today
    
    if (isPastDate) {
      classes.push('past-date')
    }
    
    // Only apply busy styling if initial data has been loaded
    if (isInitialDataLoaded) {
      const hasBusy = hasBusyTimeForDate(date)
      if (hasBusy) {
        classes.push('busy-time-date')
      }
    }
    
    return classes.join(' ')
  }, [hasBusyTimeForDate, isInitialDataLoaded])

  // Load busy time data for a specific month (only if not already loaded)
  const loadBusyTimeForMonth = useCallback(async (year: number, month: number) => {
    if (!userId) return

    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)

    const hasDataForMonth = Array.from(busyTimeByDate.keys()).some(dateKey => {
      const [yearStr, monthStr] = dateKey.split('-')
      return yearStr === year.toString() && monthStr === (month + 1).toString().padStart(2, '0')
    })

    if (hasDataForMonth) {
      return
    }

    try {
      const timezoneOffset = new Date().getTimezoneOffset() * -1

      const lstDateCheck: string[] = []
      const currentDate = new Date(firstDay)
      while (currentDate <= lastDay) {
        const yearStr = currentDate.getFullYear()
        const monthStr = (currentDate.getMonth() + 1).toString().padStart(2, '0')
        const dayStr = currentDate.getDate().toString().padStart(2, '0')
        lstDateCheck.push(`${yearStr}-${monthStr}-${dayStr}`)
        currentDate.setDate(currentDate.getDate() + 1)
      }

      const lstUserId = [userId]

      const response = isBus
        ? await busyTimeApi.getTimeSlotsForBus({
          lstSPId: lstUserId,
          lstDateCheck: lstDateCheck,
          timezone: timezoneOffset,
        })
        : await busyTimeApi.getTimeSlotsForWorker({
          lstWorkerId: lstUserId,
          lstDateCheck: lstDateCheck,
          timezone: timezoneOffset,
        })

      if (response.data && response.data.length > 0) {
        setBusyTimeByDate(prev => {
          const newBusyTimeByDate = new Map(prev)

          response.data.forEach((dateData: any) => {
            const dateKey = dateData.dateCheck

            if (dateData.lstTimeSlotWorking && dateData.lstTimeSlotWorking.length > 0) {
              const transformedTimeSlots: TimeSlotWorking[] = dateData.lstTimeSlotWorking.map(
                (item: TimeSlotWorking) => ({
                  isAvailable: item.isAvailable || false,
                  isBusyTime: item.isBusyTime || false,
                  orderConflict: item.orderConflict || 0,
                  isPastTime: item.isPastTime || false,
                  lstSPId: item.lstSPId || [],
                  start: item.start || 0,
                  end: item.end || 0,
                })
              )

              const allSlotsBusy = transformedTimeSlots.every(slot => slot.isBusyTime)
              newBusyTimeByDate.set(dateKey, {
                timeSlots: transformedTimeSlots,
                blockAllDay: allSlotsBusy
              })
            }
          })

          return newBusyTimeByDate
        })
      }
    } catch (error) {
      console.error('Error loading busy time for month:', error)
    }
  }, [userId, busyTimeByDate, isBus])

  // Helper function to convert minutes to time format (08:00 AM)
  const convertMinutesToTime = (minutes: number): string => {
    const isNextDay = minutes >= 1440
    const normalizedMinutes = isNextDay ? minutes - 1440 : minutes
    const hours = Math.floor(normalizedMinutes / 60)
    const mins = normalizedMinutes % 60
    let period = language === Languages.ar ? (hours >= 12 ? 'م' : 'ص') : (hours >= 12 ? 'PM' : 'AM')
    if (normalizedMinutes === 0) period = language === Languages.ar ? "ص" : "AM"
    const displayHours = hours % 12 || 12 // Convert 0 to 12 for 12-hour format
    const displayMins = mins.toString().padStart(2, '0')
    const displayHoursStr = displayHours.toString().padStart(2, '0')
    return `${displayHoursStr}:${displayMins} ${period}`
  }

  const isNextDayTimeSlot = (start: number, end: number): boolean => {
    return end > 1440
  }

  // Helper function to generate week days from date range (limited to first 7 days)
  const generateWeekDays = useCallback((startDate: Date | null, endDate: Date | null) => {
    if (!startDate || !endDate) return []

    const days = []
    const currentDate = new Date(startDate)
    let dayCount = 0
    const maxDays = 7 // Limit to first 7 days

    while (currentDate <= endDate && dayCount < maxDays) {
      // Get day name using translation
      const dayOfWeek = currentDate.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const dayNameKeys = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
      const dayName = t(`USERS_PAGE.STEP_UPDATE_USER.${dayNameKeys[dayOfWeek]}`)
      const dayNumber = currentDate.getDate()
      // Use same format and locale as DatePicker for month name
      const monthName = format(currentDate, 'MMMM', { locale: language === Languages.ar ? ar : en })
      // Format dateKey manually to avoid timezone issues
      const year = currentDate.getFullYear()
      const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
      const day = currentDate.getDate().toString().padStart(2, '0')
      const dateKey = `${year}-${month}-${day}`

      days.push({
        date: new Date(currentDate),
        dateKey,
        displayName: `${dayName} ${dayNumber} ${monthName}`,
        shortDisplayName: `${dayName} ${dayNumber}`,
        dayName,
        dayNumber,
        monthName
      })

      currentDate.setDate(currentDate.getDate() + 1)
      dayCount++
    }

    return days
  }, [t, language])

  const loadBusOptions = useCallback(async () => {
    // Only load bus options for Bus users, not for Workers
    if (!isBus) {
      setBusOptions([])
      return
    }
    const response = await userApi.getAllTeams({ isOnlyCentro: true })
    if (response.data) {
      setBusOptions(response.data.filter((team: Team) => team.id !== userId))
    }
  }, [userId, isBus])

  const loadWorkerOptions = useCallback(async () => {
    // Only load worker options for Worker users, not for Buses
    if (!isWorker) {
      setWorkerOptions([])
      return
    }
    try {
      const response = await userApi.getAllWorker({ userType: USER_TYPE.WORKER })
      if (response.data) {
        // Handle both ListResponse format (with rows) and direct array format
        const workersArray = Array.isArray(response.data)
          ? response.data
          : (response.data.rows || [])
        const workers = workersArray.filter((worker: UserModel) => worker.id !== userId)
        setWorkerOptions(workers)
      }
    } catch (error) {
      console.error('Error loading workers:', error)
      setWorkerOptions([])
    }
  }, [userId, isWorker])

  // Load busy time days count from API
  const loadBusyTimeDays = useCallback(async () => {
    if (!userId) return

    setIsLoadingBusyTimeDays(true)
    try {
      const timezoneOffset = new Date().getTimezoneOffset() * -1
      const response = isBus
        ? await busyTimeApi.getBusyTimeDayForBus({
          spId: userId,
          timezone: timezoneOffset
        })
        : await busyTimeApi.getBusyTimeDayForWorker({
          workerId: userId,
          timezone: timezoneOffset
        })

      if (response.data && Array.isArray(response.data)) {
        setBusyTimeDays(response.data.length)
      } else {
        setBusyTimeDays(0)
      }
    } catch (error) {
      console.error('Error loading busy time days:', error)
      setBusyTimeDays(0)
    } finally {
      setIsLoadingBusyTimeDays(false)
    }
  }, [userId, isBus])

  // Load time slots from API
  const loadTimeSlots = useCallback(async (dateToCheck?: Date) => {
    if (!userId) return // Don't load if modal is closed

    setIsLoadingTimeSlots(true)
    try {
      // Get timezone offset in minutes (e.g., 420 for UTC+7)
      const timezoneOffset = new Date().getTimezoneOffset() * -1

      // Build lstDateCheck array - load all dates in range for week view
      let lstDateCheck: string[] = []

      if (dateToCheck) {
        // If specific date provided, use only that date
        const year = dateToCheck.getFullYear()
        const month = (dateToCheck.getMonth() + 1).toString().padStart(2, '0')
        const day = dateToCheck.getDate().toString().padStart(2, '0')
        lstDateCheck = [`${year}-${month}-${day}`]
      } else if (busyDateRange[0] && busyDateRange[1]) {
        // Load all dates in the selected range
        const startDate = new Date(busyDateRange[0])
        const endDate = new Date(busyDateRange[1])

        const currentDate = new Date(startDate)
        while (currentDate <= endDate) {
          const year = currentDate.getFullYear()
          const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
          const day = currentDate.getDate().toString().padStart(2, '0')
          lstDateCheck.push(`${year}-${month}-${day}`)
          currentDate.setDate(currentDate.getDate() + 1)
        }
      } else if (selectedDate && selectedDate[0]) {
        // Fallback to selected date
        const year = selectedDate[0].getFullYear()
        const month = (selectedDate[0].getMonth() + 1).toString().padStart(2, '0')
        const day = selectedDate[0].getDate().toString().padStart(2, '0')
        lstDateCheck = [`${year}-${month}-${day}`]
      }

      // LstSPId is the current user ID from the modal
      const lstSPId = [userId]

      const response = isBus
        ? await busyTimeApi.getTimeSlotsForBus({
          lstSPId: lstSPId,
          lstDateCheck: lstDateCheck,
          timezone: timezoneOffset,
        })
        : await busyTimeApi.getTimeSlotsForWorker({
          lstWorkerId: lstSPId,
          lstDateCheck: lstDateCheck,
          timezone: timezoneOffset,
        })

      if (response.data && response.data.length > 0) {
        // Process each date's time slots
        const newBusyTimeByDate = new Map(busyTimeByDate)
        const newWeekDayStatuses = { ...weekDayStatuses }

        response.data.forEach((dateData: any, index: number) => {
          // Never default to lstDateCheck[0]: missing dateCheck would assign wrong day's slots to one dateKey.
          const dateKey = dateData.dateCheck ?? lstDateCheck[index]
          if (!dateKey) {
            return
          }
          if (dateData.lstTimeSlotWorking && dateData.lstTimeSlotWorking.length > 0) {
            // Transform API data to TimeSlot format
            const transformedTimeSlots: TimeSlotWorking[] = dateData.lstTimeSlotWorking.map(
              (item: TimeSlotWorking) => ({
                isAvailable: item.isAvailable || false,
                isBusyTime: item.isBusyTime || false,
                orderConflict: item.orderConflict || 0,
                isPastTime: item.isPastTime || false,
                lstSPId: item.lstSPId || [],
                start: item.start || 0,
                end: item.end || 0,
              })
            )

            // Save to Map
            const allSlotsBusy = transformedTimeSlots.every(slot => slot.isBusyTime)
            newBusyTimeByDate.set(dateKey, {
              timeSlots: transformedTimeSlots,
              blockAllDay: allSlotsBusy
            })

            // Update week day status based on time slot states
            const allBusy = transformedTimeSlots.every(slot => slot.isBusyTime)
            const allOff = transformedTimeSlots.every(slot => !slot.isAvailable && !slot.isBusyTime)
            const allAvailable = transformedTimeSlots.every(slot => slot.isAvailable && !slot.isBusyTime)
            const hasBusy = transformedTimeSlots.some(slot => slot.isBusyTime)
            const hasAvailable = transformedTimeSlots.some(slot => slot.isAvailable && !slot.isBusyTime)

            let newStatus: BusyStatusType
            if (allBusy) {
              newStatus = BusyStatus.BUSY
            } else if (allOff) {
              newStatus = BusyStatus.OFF
            } else if (allAvailable) {
              newStatus = BusyStatus.AVAILABLE
            } else if (hasBusy && hasAvailable) {
              newStatus = BusyStatus.PARTIALLY_BUSY
            } else {
              newStatus = BusyStatus.AVAILABLE // Default fallback
            }

            newWeekDayStatuses[dateKey] = newStatus

            // If this is the currently selected date, update the UI
            if (dateToCheck || (selectedDate && selectedDate[0] && dateKey === `${selectedDate[0].getFullYear()}-${(selectedDate[0].getMonth() + 1).toString().padStart(2, '0')}-${selectedDate[0].getDate().toString().padStart(2, '0')}`)) {
              setTimeSlots(transformedTimeSlots)
              setBlockAllDay(allSlotsBusy) // Sync state
              setBlockAllDayDisplay(allSlotsBusy) // Update display only, not trigger data changes
              setHasEmptyTimeSlots(false) // Set to false if time slots are found
            }
          } else {
            // No time slots for this date - still save to busyTimeByDate with empty slots
            newBusyTimeByDate.set(dateKey, {
              timeSlots: [],
              blockAllDay: false
            })
            newWeekDayStatuses[dateKey] = BusyStatus.OFF

            // If this is the currently selected date, update the UI
            if (dateToCheck || (selectedDate && selectedDate[0] && dateKey === `${selectedDate[0].getFullYear()}-${(selectedDate[0].getMonth() + 1).toString().padStart(2, '0')}-${selectedDate[0].getDate().toString().padStart(2, '0')}`)) {
              setTimeSlots([])
              setBlockAllDay(false)
              setBlockAllDayDisplay(false) // Sync display state
              setHasEmptyTimeSlots(true)
            }
          }
        })

        // Update the maps
        setBusyTimeByDate(newBusyTimeByDate)
        setWeekDayStatuses(newWeekDayStatuses)

        // If no specific date was requested, use the first date's time slots for display
        if (!dateToCheck && response.data[0] && response.data[0].lstTimeSlotWorking && response.data[0].lstTimeSlotWorking.length > 0) {
          const firstDateSlots = response.data[0].lstTimeSlotWorking.map((item: TimeSlotWorking) => ({
            isAvailable: item.isAvailable || false,
            isBusyTime: item.isBusyTime || false,
            orderConflict: item.orderConflict || 0,
            isPastTime: item.isPastTime || false,
            lstSPId: item.lstSPId || [],
            start: item.start || 0,
            end: item.end || 0,
          }))
          setTimeSlots(firstDateSlots)
          const allSlotsBusy = firstDateSlots.every((slot: TimeSlotWorking) => slot.isBusyTime)
          setBlockAllDay(allSlotsBusy)
          setBlockAllDayDisplay(allSlotsBusy) // Sync display state
          setHasEmptyTimeSlots(false) // Set to false if time slots are found
        } else if (!dateToCheck) {
          // No time slots found for any date
          setTimeSlots([])
          setBlockAllDay(false)
          setBlockAllDayDisplay(false) // Sync display state
          setHasEmptyTimeSlots(true) // Set to true if no time slots found
        }
      } else {
        // API returns empty - mark all dates as OFF
        setTimeSlots([])
        setBlockAllDay(false)
        setBlockAllDayDisplay(false) // Sync display state
        setHasEmptyTimeSlots(true) // Set to true if API returns empty

        // Update week day statuses to OFF for all dates in range
        lstDateCheck.forEach(dateKey => {
          setWeekDayStatuses(prev => ({
            ...prev,
            [dateKey]: BusyStatus.OFF
          }))
        })
      }
    } catch (error) {
      console.error('Error loading time slots:', error)
      // Fallback to default time slots on error
      setTimeSlots([])
      setHasEmptyTimeSlots(false) // Don't show "No working hour available" on error
      toast.error(t('Failed to load time slots'), ToastDefaultConfig())
    } finally {
      setIsLoadingTimeSlots(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, userId, busyDateRange, selectedDate])

  // Handlers
  const resetForm = useCallback(() => {
    setSelectedBuses([])
    setSelectedWorkers([])
    setBusyDateRange(getDefaultDateRange())
    setBlockAllDay(false)
    setBlockAllWeekDisplay(false)
    setActiveTab('day')
    setIsRepeated(false)

    // Clear busy time by date map
    setBusyTimeByDate(new Map())

    // Clear repeated settings by date
    setRepeatedByDate(new Map())
    setRepeatedRangeByDate(new Map())

    // Clear selected time slots by date
    setSelectedTimeSlotsByDate(new Map())

    // Reset initial data loaded flag
    setIsInitialDataLoaded(false)

    // Initialize week day statuses - all start as available
    const weekDays = generateWeekDays(getDefaultDateRange()[0], getDefaultDateRange()[1])
    const initialStatuses: { [key: string]: BusyStatusType } = {}
    weekDays.forEach(day => {
      initialStatuses[day.dateKey] = BusyStatus.AVAILABLE
    })
    setWeekDayStatuses(initialStatuses)

    // Reset time slots to non-busy state but keep the loaded data
    // Only reset if there are no time slots loaded yet
    setTimeSlots((prev) => {
      if (prev.length === 0) {
        return prev // Don't reset if no time slots
      }
      // Preserve OFF slots (isAvailable=false & isBusyTime=false)
      return prev.map((slot) => {
        const isOff = !slot.isAvailable && !slot.isBusyTime
        if (isOff) return slot
        return { ...slot, isBusyTime: false, isAvailable: true }
      })
    })

    // Reset empty time slots flag
    setHasEmptyTimeSlots(false)

    // Reset busy time days to null (will be loaded from API)
    setBusyTimeDays(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load busy time data from today to end of current month
  const loadBusyTimeFromToday = useCallback(async () => {
    if (!userId) return

    try {
      const timezoneOffset = new Date().getTimezoneOffset() * -1
      const today = new Date()
      const currentMonth = today.getMonth()
      const currentYear = today.getFullYear()

      // Get today and last day of current month
      const todayDate = new Date(today)
      const lastDay = new Date(currentYear, currentMonth + 1, 0)

      // Generate dates from today to end of current month only
      const lstDateCheck: string[] = []
      const currentDate = new Date(todayDate)

      // Reset time to avoid timezone issues
      currentDate.setHours(0, 0, 0, 0)
      lastDay.setHours(23, 59, 59, 999)

      while (currentDate <= lastDay) {
        const year = currentDate.getFullYear()
        const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
        const day = currentDate.getDate().toString().padStart(2, '0')
        lstDateCheck.push(`${year}-${month}-${day}`)
        currentDate.setDate(currentDate.getDate() + 1)
      }

      const lstUserId = [userId]

      const response = isBus
        ? await busyTimeApi.getTimeSlotsForBus({
          lstSPId: lstUserId,
          lstDateCheck: lstDateCheck,
          timezone: timezoneOffset,
        })
        : await busyTimeApi.getTimeSlotsForWorker({
          lstWorkerId: lstUserId,
          lstDateCheck: lstDateCheck,
          timezone: timezoneOffset,
        })

      if (response.data && response.data.length > 0) {
        setBusyTimeByDate(prev => {
          const newBusyTimeByDate = new Map(prev)

          response.data.forEach((dateData: any) => {
            const dateKey = dateData.dateCheck

            if (dateData) {
              const transformedTimeSlots: TimeSlotWorking[] = dateData.lstTimeSlotWorking.map(
                (item: TimeSlotWorking) => ({
                  isAvailable: item.isAvailable || false,
                  isBusyTime: item.isBusyTime || false,
                  orderConflict: item.orderConflict || 0,
                  isPastTime: item.isPastTime || false,
                  lstSPId: item.lstSPId || [],
                  start: item.start || 0,
                  end: item.end || 0,
                })
              )

              const allSlotsBusy = transformedTimeSlots.length > 0 && transformedTimeSlots.every(slot => slot.isBusyTime)
              newBusyTimeByDate.set(dateKey, {
                timeSlots: transformedTimeSlots,
                blockAllDay: allSlotsBusy
              })
            }
          })

          return newBusyTimeByDate
        })

        // Mark initial data as loaded
        setIsInitialDataLoaded(true)
      } else {
        // Still mark as loaded even if no data
        setIsInitialDataLoaded(true)
      }
    } catch (error) {
      console.error('Error loading busy time from today:', error)
      // Mark as loaded even on error to prevent infinite loading
      setIsInitialDataLoaded(true)
    }
  }, [userId, isBus])

  // Load time slots when modal opens or date range changes
  useEffect(() => {
    if (userId && busyDateRange[0] && isInitialDataLoaded) {
      // Check if we have data for all dates in the range
      const startDate = busyDateRange[0]
      const endDate = busyDateRange[1] || busyDateRange[0]

      const datesInRange: string[] = []
      const currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        const year = currentDate.getFullYear()
        const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
        const day = currentDate.getDate().toString().padStart(2, '0')
        datesInRange.push(`${year}-${month}-${day}`)
        currentDate.setDate(currentDate.getDate() + 1)
      }

      // Check if we have data for all dates
      const hasAllData = datesInRange.every(dateKey => busyTimeByDate.has(dateKey))

      if (!hasAllData) {
        // Load time slots for all dates in range
        loadTimeSlots()
      } else {
        // Load from saved data for the selected date
        if (selectedDate && selectedDate[0]) {
          const year = selectedDate[0].getFullYear()
          const month = (selectedDate[0].getMonth() + 1).toString().padStart(2, '0')
          const day = selectedDate[0].getDate().toString().padStart(2, '0')
          const dateKey = `${year}-${month}-${day}`

          const savedBusyTime = busyTimeByDate.get(dateKey)
          if (savedBusyTime) {
            if (savedBusyTime.timeSlots.length > 0) {
              setTimeSlots(savedBusyTime.timeSlots)
              setBlockAllDay(savedBusyTime.blockAllDay)
              setBlockAllDayDisplay(savedBusyTime.blockAllDay) // Sync display state
            } else {
              setTimeSlots([])
              setBlockAllDay(savedBusyTime.blockAllDay) // Sync state
              setBlockAllDayDisplay(savedBusyTime.blockAllDay)
              setHasEmptyTimeSlots(true)
            }
          } else {
            // If no saved data, load from API for this specific date
            loadTimeSlots(selectedDate[0])
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyDateRange, userId, loadTimeSlots, selectedDate, isInitialDataLoaded])

  useEffect(() => {
    if (userId) {
      resetForm()
      loadBusOptions()
      loadWorkerOptions()
      loadBusyTimeFromToday()
      loadBusyTimeDays()
    }
  }, [userId, loadBusOptions, loadWorkerOptions, resetForm, loadBusyTimeFromToday, loadBusyTimeDays])

  const handleClose = () => {
    resetForm()
    onHide?.()
  }

  const handleBusChange = (selectedOptions: any[]) => {
    // MultiSelect returns [{label, value}], convert back to Team[]
    const selectedTeams = selectedOptions
      .map((option) => busOptions.find((bus) => bus.id === option.value))
      .filter((team): team is Team => team !== undefined)
    setSelectedBuses(selectedTeams)
  }

  const handleWorkerChange = (selectedOptions: any[]) => {
    // MultiSelect returns [{label, value}], convert back to UserModel[]
    const selectedWorkerList = selectedOptions
      .map((option) => workerOptions.find((worker) => worker.id === option.value))
      .filter((worker): worker is UserModel => worker !== undefined)
    setSelectedWorkers(selectedWorkerList)
  }

  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    // Only save current time slots for day view when changing to a different date
    // Week view doesn't have a concept of "selectedDate" - it works with date ranges
    if (activeTab === 'day' && selectedDate && selectedDate[0] && timeSlots.length > 0 && dates[0] && dates[1]) {
      const currentDateKey = createDateKey(selectedDate[0])
      const newDateKey = createDateKey(dates[0])

      if (currentDateKey !== newDateKey) {
        setBusyTimeByDate(prev => {
          const newMap = new Map(prev)
          const lastIdx = timeSlots.length - 1
          const timeSlotsWithLastBlock = timeSlots.map((slot, idx) => ({
            ...slot,
            IsLastBlock: idx === lastIdx,
          }))
          newMap.set(currentDateKey, {
            timeSlots: timeSlotsWithLastBlock,
            blockAllDay: blockAllDay
          })
          return newMap
        })
      }
    }

    setTempDateRange(dates)

    // Only apply time slots when both dates are selected (date from and date to)
    if (dates[0] && dates[1]) {
      // Reset toggles to off when date range changes
      setBlockAllDayDisplay(false)
      setBlockAllWeekDisplay(false)

      // Handle same date selection (double click)
      if (dates[0].getFullYear() === dates[1].getFullYear() &&
        dates[0].getMonth() === dates[1].getMonth() &&
        dates[0].getDate() === dates[1].getDate()) {

        const d0 = dates[0]
        setBusyDateRange([d0, d0])

        // Load from cache first to update toggles immediately
        const dateKey = createDateKey(d0)
        const cached = busyTimeByDate.get(dateKey)

        if (cached && cached.timeSlots && cached.timeSlots.length > 0) {
          // Load from cache - immediate toggle update
          setTimeSlots(cached.timeSlots)
          setBlockAllDayDisplay(cached.blockAllDay)
          setWeekDayStatuses(prev => ({
            ...prev,
            [dateKey]: computeStatusFromSlots(cached.timeSlots)
          }))

          // Sync "Block all week" toggle (will be false for single day unless all slots are busy)
          recomputeBlockAllWeek(d0, d0)
        } else {
          // No cache - set optimistic states
          setTimeSlots([])
          setBlockAllDay(false)
          setWeekDayStatuses(prev => ({
            ...prev,
            [dateKey]: BusyStatus.AVAILABLE
          }))
          setBlockAllWeekDisplay(false)
        }

        // Always call API to get fresh data for the selected date impedance
        // This ensures we have the latest status and time slots after cache display
        //loadTimeSlots(d0)

        // Auto-close the date picker popper on single-day selection (double-click)
        setIsDatePickerOpen(false)
      } else {
        // Different dates - apply range
        setBusyDateRange([dates[0], dates[1]])

        // Load cached data for the first date if available
        const newDateKey = createDateKey(dates[0])
        const cachedData = busyTimeByDate.get(newDateKey)

        if (cachedData && cachedData.timeSlots && cachedData.timeSlots.length > 0) {
          setTimeSlots(cachedData.timeSlots)
          setBlockAllDay(cachedData.blockAllDay)
          setWeekDayStatuses(prev => ({
            ...prev,
            [newDateKey]: computeStatusFromSlots(cachedData.timeSlots)
          }))
        } else {
          setTimeSlots([])
          setBlockAllDay(false)
        }

        // Sync "Block all week" toggle with the selected range from cache
        recomputeBlockAllWeek(dates[0], dates[1])
      }
    }
  }

  const handleDateRangeApply = () => {
    // Validation: Must have both "date from" and "date to" for both day and week view
    if (!tempDateRange || !tempDateRange[0] || !tempDateRange[1]) {
      toast.error(t('USERS_PAGE.STEP_UPDATE_USER.BUSY_DATE_RANGE_REQUIRED'), ToastDefaultConfig())
      return
    }

    if (selectedDate && selectedDate[0] && timeSlots.length > 0) {
      const currentDateKey = createDateKey(selectedDate[0])

      setBusyTimeByDate(prev => {
        const newMap = new Map(prev)
        newMap.set(currentDateKey, {
          timeSlots: timeSlots,
          blockAllDay: blockAllDay
        })

        return newMap
      })
    }

    // Apply the temporary date range to the actual state
    setBusyDateRange(tempDateRange)

    // Reset toggles to off when date range changes
    setBlockAllDayDisplay(false)
    setBlockAllWeekDisplay(false)

    // Don't load time slots when date range is selected - use cached data
    if (tempDateRange && tempDateRange[0] && tempDateRange[1]) {
      const startDate = tempDateRange[0]
      const endDate = tempDateRange[1]

      // Sync toggles from cache
      recomputeBlockAllWeek(startDate, endDate)
      
      // Load time slots for the selected date from cache if available
      if (selectedDate && selectedDate[0]) {
        const year = selectedDate[0].getFullYear()
        const month = (selectedDate[0].getMonth() + 1).toString().padStart(2, '0')
        const day = selectedDate[0].getDate().toString().padStart(2, '0')
        const dateKey = `${year}-${month}-${day}`
        
        const savedBusyTime = busyTimeByDate.get(dateKey)
        if (savedBusyTime && savedBusyTime.timeSlots.length > 0) {
          setTimeSlots(savedBusyTime.timeSlots)
          setBlockAllDay(savedBusyTime.blockAllDay)
          setBlockAllDayDisplay(savedBusyTime.blockAllDay)
        }
      }
    }
  }

  const handleTabChange = (newTab: 'day' | 'week') => {
    setActiveTab(newTab)

    // When switching to week view, update week day status for current selected date
    if (newTab === 'week' && selectedDate && selectedDate[0]) {
      const dateKey = createDateKey(selectedDate[0])

      // Save current time slots to busyTimeByDate if not already saved
      setBusyTimeByDate(prev => {
        const newMap = new Map(prev)
        if (!newMap.has(dateKey)) {
          newMap.set(dateKey, {
            timeSlots: timeSlots,
            blockAllDay: blockAllDay
          })
        }
        return newMap
      })

      // Calculate and set status for current date based on time slots
      let newStatus: BusyStatusType
      if (timeSlots.length === 0) {
        // No working hours means OFF status
        newStatus = BusyStatus.OFF
      } else {
        const allBusy = timeSlots.every(slot => slot.isBusyTime)
        const allOff = timeSlots.every(slot => !slot.isAvailable && !slot.isBusyTime)
        const allAvailable = timeSlots.every(slot => slot.isAvailable && !slot.isBusyTime)
        const hasBusy = timeSlots.some(slot => slot.isBusyTime)
        const hasAvailable = timeSlots.some(slot => slot.isAvailable && !slot.isBusyTime)

        if (allBusy) {
          newStatus = BusyStatus.BUSY
        } else if (allOff) {
          newStatus = BusyStatus.OFF
        } else if (allAvailable) {
          newStatus = BusyStatus.AVAILABLE
        } else if (hasBusy && hasAvailable) {
          newStatus = BusyStatus.PARTIALLY_BUSY
        } else {
          newStatus = BusyStatus.AVAILABLE
        }
      }

      // Update week day status for the current date
      setWeekDayStatuses(prev => ({
        ...prev,
        [dateKey]: newStatus
      }))

      // Ensure we have a range for week view
      if (!busyDateRange[0] || !busyDateRange[1]) {
        setBusyDateRange([selectedDate[0], selectedDate[0]])
      }
      // Don't load time slots - use cached data
      // Sync toggles from cache
      if (busyDateRange[0] && busyDateRange[1]) {
        recomputeBlockAllWeek(busyDateRange[0], busyDateRange[1])
      }
    }

    // When switching to day view, load time slots from cache if available
    if (newTab === 'day' && selectedDate && selectedDate[0]) {
      const year = selectedDate[0].getFullYear()
      const month = (selectedDate[0].getMonth() + 1).toString().padStart(2, '0')
      const day = selectedDate[0].getDate().toString().padStart(2, '0')
      const dateKey = `${year}-${month}-${day}`
      
      const savedBusyTime = busyTimeByDate.get(dateKey)
      if (savedBusyTime && savedBusyTime.timeSlots.length > 0) {
        setTimeSlots(savedBusyTime.timeSlots)
        setBlockAllDay(savedBusyTime.blockAllDay)
        setBlockAllDayDisplay(savedBusyTime.blockAllDay)
      }
    }
  }

  const handleRepeatedToggle = (dateKey: string, isRepeated: boolean) => {
    setRepeatedByDate(prev => {
      const newMap = new Map(prev)
      newMap.set(dateKey, isRepeated)
      return newMap
    })
  }

  const handleTimeSlotToggle = (start: number, end: number) => {
    setTimeSlots((prev) => {
      const updatedSlots = prev.map((slot) =>
        slot.start === start && slot.end === end
          ? (() => {
            // OFF slots should not be toggleable
            const isOff = !slot.isAvailable && !slot.isBusyTime
            if (isOff) return slot

            const newIsBusyTime = !slot.isBusyTime
            return {
              ...slot,
              isBusyTime: newIsBusyTime,
              isAvailable: !newIsBusyTime,
            }
          })()
          : slot
      )

      // Check if all WORKING slots are busy (ignore OFF slots)
      const workingSlots = updatedSlots.filter(slot => slot.isAvailable || slot.isBusyTime)
      const allWorkingSlotsBusy = workingSlots.length > 0 && workingSlots.every(slot => slot.isBusyTime)
      setBlockAllDay(allWorkingSlotsBusy)
      setBlockAllDayDisplay(allWorkingSlotsBusy) // Update display toggle as well

      // Save to Map for current date
      if (selectedDate && selectedDate[0]) {
        const dateKey = createDateKey(selectedDate[0])
        
        // Mark this date as modified in current session
        setModifiedDatesInSession(prev => new Set(prev).add(dateKey))
        
        // Check if current date has repeated ON and is part of a range
        const isCurrentDateRepeated = repeatedByDate.get(dateKey) || false
        const storedRange = repeatedRangeByDate.get(dateKey)

        setBusyTimeByDate(prev => {
          const newMap = new Map(prev)
          newMap.set(dateKey, {
            timeSlots: updatedSlots,
            blockAllDay: allWorkingSlotsBusy
          })
          
          // If repeated is ON and we have a stored range, copy time slots to all dates in range
          if (isCurrentDateRepeated && storedRange && activeTab === 'day') {
            const [startDate, endDate] = storedRange
            const cursor = new Date(startDate)
            while (cursor <= endDate) {
              const year = cursor.getFullYear()
              const month = (cursor.getMonth() + 1).toString().padStart(2, '0')
              const day = cursor.getDate().toString().padStart(2, '0')
              const rangeDateKey = `${year}-${month}-${day}`
              
              // Mark all dates in range as modified
              setModifiedDatesInSession(prev => new Set(prev).add(rangeDateKey))
              
              // Copy time slots to all dates in range (except current date which is already set)
              if (rangeDateKey !== dateKey) {
                newMap.set(rangeDateKey, {
                  timeSlots: updatedSlots.map(slot => ({ ...slot })),
                  blockAllDay: allWorkingSlotsBusy
                })
              }
              
              cursor.setDate(cursor.getDate() + 1)
            }
          }
          
          return newMap
        })

        // Save selected time slots for this date
        const selectedSlots = updatedSlots.filter(slot => slot.isBusyTime)
        setSelectedTimeSlotsByDate(prev => {
          const newMap = new Map(prev)
          newMap.set(dateKey, selectedSlots)
          
          // If repeated is ON, also save for all dates in range
          if (isCurrentDateRepeated && storedRange && activeTab === 'day') {
            const [startDate, endDate] = storedRange
            const cursor = new Date(startDate)
            while (cursor <= endDate) {
              const year = cursor.getFullYear()
              const month = (cursor.getMonth() + 1).toString().padStart(2, '0')
              const day = cursor.getDate().toString().padStart(2, '0')
              const rangeDateKey = `${year}-${month}-${day}`
              
              if (rangeDateKey !== dateKey) {
                newMap.set(rangeDateKey, selectedSlots.map(slot => ({ ...slot })))
              }
              
              cursor.setDate(cursor.getDate() + 1)
            }
          }
          
          return newMap
        })
      }

      return updatedSlots
    })
  }


  // useEffect for blockAllDay removed - now handled directly in onChange

  // useEffect for blockAllWeek removed - now handled directly in onChange

  // Sync toggles after data is loaded
  useEffect(() => {
    // Only sync when not loading and date range is set
    if (!isLoadingTimeSlots && busyDateRange[0] && busyDateRange[1]) {
      // Sync Block All Week toggle based on current cache
      recomputeBlockAllWeek(busyDateRange[0], busyDateRange[1])
    }
  }, [isLoadingTimeSlots, busyDateRange, recomputeBlockAllWeek])

  // Sync blockAllDayDisplay when initial data is loaded and we have data for current date
  useEffect(() => {
    if (isInitialDataLoaded && selectedDate && selectedDate[0] && busyTimeByDate.size > 0) {
      const year = selectedDate[0].getFullYear()
      const month = (selectedDate[0].getMonth() + 1).toString().padStart(2, '0')
      const day = selectedDate[0].getDate().toString().padStart(2, '0')
      const dateKey = `${year}-${month}-${day}`

      const savedBusyTime = busyTimeByDate.get(dateKey)
      if (savedBusyTime && savedBusyTime.timeSlots.length > 0) {
        // Check if all slots are busy
        const allSlotsBusy = savedBusyTime.timeSlots.every(slot => slot.isBusyTime)
        // Only update if values are different to avoid unnecessary re-renders
        if (blockAllDay !== allSlotsBusy || blockAllDayDisplay !== allSlotsBusy) {
          setBlockAllDay(allSlotsBusy)
          setBlockAllDayDisplay(allSlotsBusy)
        }
      }
    }
  }, [isInitialDataLoaded, selectedDate, busyTimeByDate, blockAllDay, blockAllDayDisplay])

  const handleResetAllBusyTime = () => {
    if (!userId) return
    setShowConfirmModal(true)
  }

  const confirmResetAllBusyTime = async () => {
    if (!userId) return

    setIsLoading(true)

    const userIds = isBus
      ? [userId, ...selectedBuses.map(bus => bus.id).filter((id): id is string => !!id)]
      : [userId, ...selectedWorkers.map(worker => worker.id).filter((id): id is string => !!id)]

    try {
      setShowConfirmModal(false)

      const response = isBus
        ? await busyTimeApi.resetAllBusyTimeForBus(userIds)
        : await busyTimeApi.resetAllBusyTimeForWorker(userIds)

      if (response.status === HTTP_RESPONSE_STATUS.SUCCESS) {
        toast.success(t('USERS_PAGE.STEP_UPDATE_USER.ALL_BUSY_TIMES_RESET_SUCCESS'), ToastDefaultConfig())

        // Reload busy time days count after successful reset
        loadBusyTimeDays()

        handleClose();
      } else {
        toast.error(response.message || t('USERS_PAGE.STEP_UPDATE_USER.FAILED_TO_RESET_BUSY_TIMES'), ToastDefaultConfig())
      }
    } catch (error: any) {
      console.error('Error resetting busy times:', error)
      const errorMessage = error.response?.data?.message
        || error.response?.data?.title
        || error.message
        || t('USERS_PAGE.STEP_UPDATE_USER.FAILED_TO_RESET_BUSY_TIMES')
      toast.error(errorMessage, ToastDefaultConfig())
    } finally {
      setIsLoading(false)
    }
  }

  const formatDateRange = (startDate: Date | null, endDate: Date | null) => {
    const formatDate = (date: Date) => {
      const day = date.getDate()
      const month = date.toLocaleString('default', { month: 'long' })
      const year = date.getFullYear()
      return `${day} ${month} ${year}`
    }

    if (!startDate && !endDate) return ''
    if (startDate && !endDate) return `${formatDate(startDate)} - Select end date`
    if (!startDate && endDate) return `Select start date - ${formatDate(endDate)}`
    if (startDate && endDate) {
      // Check if start and end dates are the same day
      const isSameDay = startDate.toDateString() === endDate.toDateString()
      if (isSameDay) {
        return formatDate(startDate)
      }
      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }
    return ''
  }

  // Helper function to convert minutes to time string with date (yyyy-MM-dd HH:mm)
  const convertMinutesToDateTimeString = (minutes: number, date: Date): string => {
    const totalHours = Math.floor(minutes / 60)
    const mins = minutes % 60
    const actualHours = totalHours % 24
    const daysToAdd = Math.floor(totalHours / 24)

    // Create a new date object to avoid mutating the original
    const resultDate = new Date(date)
    resultDate.setDate(resultDate.getDate() + daysToAdd)

    const year = resultDate.getFullYear()
    const month = (resultDate.getMonth() + 1).toString().padStart(2, '0')
    const day = resultDate.getDate().toString().padStart(2, '0')
    const hoursStr = actualHours.toString().padStart(2, '0')
    const minsStr = mins.toString().padStart(2, '0')

    const result = `${year}-${month}-${day} ${hoursStr}:${minsStr}`
    return result
  }

  const handleSubmit = async () => {
    if (!userId) return

    setIsLoading(true)

    try {
      const timezoneOffset = new Date().getTimezoneOffset() * -1

      // For Bus: use SPIds (comma-separated string)
      // For Worker: use WorkerId and ApplyToWorkerIds (array)
      const spIdsArray = isBus
        ? [userId, ...selectedBuses.map(bus => bus.id).filter((id): id is string => !!id)]
        : [userId, ...selectedWorkers.map(worker => worker.id).filter((id): id is string => !!id)]
      const spIds = spIdsArray.join(',')

      // For Worker: separate main worker ID and apply-to worker IDs
      const applyToWorkerIds = isWorker
        ? selectedWorkers.map(worker => worker.id).filter((id): id is string => !!id)
        : []

      if (activeTab === 'week') {
        // Handle week view submission
        const weekDays = generateWeekDays(busyDateRange[0], busyDateRange[1])

        // Analyze each day's time slots to determine status
        const daysWithAnalysis = weekDays.map(day => {
          const savedData = busyTimeByDate.get(day.dateKey)
          const slots = savedData?.timeSlots || []

          if (slots.length === 0) {
            return { ...day, status: BusyStatus.OFF, hasBusySlots: false, hasAvailableSlots: false }
          }

          const hasBusySlots = slots.some(slot => slot.isBusyTime)
          const hasAvailableSlots = slots.some(slot => slot.isAvailable && !slot.isBusyTime)
          const allBusy = slots.every(slot => slot.isBusyTime)
          const allAvailable = slots.every(slot => slot.isAvailable && !slot.isBusyTime)

          let status: BusyStatusType
          if (allBusy) {
            status = BusyStatus.BUSY
          } else if (allAvailable) {
            status = BusyStatus.AVAILABLE
          } else if (hasBusySlots && hasAvailableSlots) {
            status = BusyStatus.PARTIALLY_BUSY
          } else {
            status = BusyStatus.OFF
          }

          return { ...day, status, hasBusySlots, hasAvailableSlots, slots }
        })

        const modifiedDays = daysWithAnalysis.filter(day =>
          day.status === BusyStatus.BUSY ||
          day.status === BusyStatus.AVAILABLE ||
          day.status === BusyStatus.PARTIALLY_BUSY
        )

        if (!busyDateRange[0]) {
          toast.error(t('USERS_PAGE.STEP_UPDATE_USER.BUSY_DATE_FROM_REQUIRED'), ToastDefaultConfig())
          setIsLoading(false)
          return
        }

        if (modifiedDays.length === 0) {
          toast.error(t('USERS_PAGE.STEP_UPDATE_USER.BUSY_TIME_SLOT_REQUIRED'), ToastDefaultConfig())
          setIsLoading(false)
          return
        }

        // Create busy time objects based on Repeated setting
        let busyTimeObjects: BusyTimeSPObject[] = []
        let dateCheckArray: string[] = []
        let numOfRepeated = 0 // Number of weeks that have busy days with repeated

        // Check if any of the selected days have Repeated ON (consider all analyzed days, not only busy)
        const hasRepeatedDays = daysWithAnalysis.some(day => {
          const isDateRepeated = repeatedByDate.get(day.dateKey) || false
          return isDateRepeated
        })

        if (hasRepeatedDays) {
          // If any day has Repeated ON: apply pattern to all dates in range (all weeks within the range)
          // BusyTimeObjects will only include matching weekdays in the first 7 days
          // DateCheck will include 7 days from the first date
          const allDatesInRange: Date[] = []
          const startDate = new Date(busyDateRange[0]!)
          const endDate = new Date(busyDateRange[1]!)
          const currentDate = new Date(startDate)

          while (currentDate <= endDate) {
            allDatesInRange.push(new Date(currentDate))
            currentDate.setDate(currentDate.getDate() + 1)
          }

          // First 7 days from the start date (for BusyTimeObjects)
          const first7Days: Date[] = []
          const firstDate = new Date(busyDateRange[0]!)
          for (let i = 0; i < 7; i++) {
            const date = new Date(firstDate)
            date.setDate(firstDate.getDate() + i)
            first7Days.push(date)
          }

          // Get the day of week pattern from selected days that have Repeated ON
          // Only consider BUSY and AVAILABLE days (PARTIALLY_BUSY are excluded from repeated pattern)
          const repeatedDays = daysWithAnalysis.filter(day => {
            const isDateRepeated = repeatedByDate.get(day.dateKey) || false
            const isBusyOrAvailable = day.status === BusyStatus.BUSY || day.status === BusyStatus.AVAILABLE
            return isDateRepeated && isBusyOrAvailable
          })
          const selectedDayOfWeeks = repeatedDays.map(day => day.date.getDay())

          // Calculate numOfRepeated: number of weeks that have busy days (based on repeated pattern)
          // Find all matching weekdays in range and count unique weeks
          const weeksWithBusyDays = new Set<number>()
          allDatesInRange.forEach(date => {
            const dayOfWeek = date.getDay()
            if (selectedDayOfWeeks.includes(dayOfWeek)) {
              // Calculate week number: get the start of the week (Sunday) and calculate week number
              const weekStart = new Date(date)
              weekStart.setDate(date.getDate() - date.getDay()) // Set to Sunday of that week
              const weekKey = Math.floor(weekStart.getTime() / (1000 * 60 * 60 * 24 * 7))
              weeksWithBusyDays.add(weekKey)
            }
          })
          numOfRepeated = weeksWithBusyDays.size

          // DateCheck for week view: 7 days starting from the first date of the date range
          if (busyDateRange[0]) {
            const firstDate = new Date(busyDateRange[0])
            for (let i = 0; i < 7; i++) {
              const date = new Date(firstDate)
              date.setDate(firstDate.getDate() + i)
              const year = date.getFullYear()
              const month = (date.getMonth() + 1).toString().padStart(2, '0')
              const dayNum = date.getDate().toString().padStart(2, '0')
              const dateKey = `${year}-${month}-${dayNum}`
              dateCheckArray.push(dateKey)
            }
          }

          // Apply pattern to first 7 days only - create BusyTimeObjects for all busy time slots in first 7 days
          // Backend will use these to set busy time for all repeated days based on NumOfRepeat
          first7Days.forEach(date => {
            const year = date.getFullYear()
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const dayNum = date.getDate().toString().padStart(2, '0')
            const dateKey = `${year}-${month}-${dayNum}`
            
            // Find the day in daysWithAnalysis
            const dayInfo = daysWithAnalysis.find(day => day.dateKey === dateKey)
            
            if (dayInfo) {
              // Get time slots for this date
              let slotsToUse: TimeSlotWorking[] = []
              
              // First try to get slots from dayInfo (from daysWithAnalysis)
              if ('slots' in dayInfo && dayInfo.slots && dayInfo.slots.length > 0) {
                slotsToUse = dayInfo.slots
              } else {
                // Fallback to busyTimeByDate
                const savedData = busyTimeByDate.get(dateKey)
                slotsToUse = savedData?.timeSlots || []
              }
              
              // Add all busy time slots for this date (including PARTIALLY_BUSY days)
              const busySlotsForDay = slotsToUse.filter((slot: TimeSlotWorking) => slot.isBusyTime)
              busySlotsForDay.forEach((slot: TimeSlotWorking, idx) => {
                const isLastBlock = idx === busySlotsForDay.length - 1
                busyTimeObjects.push({
                  Start: convertMinutesToDateTimeString(slot.start, date),
                  End: convertMinutesToDateTimeString(slot.end, date),
                  SpIds: spIds,
                  ...(isLastBlock ? { IsLastBlock: true } : {}),
                })
              })
            }
          })

          // Note: All busy time slots from first 7 days have already been added above
          // (including BUSY, AVAILABLE, and PARTIALLY_BUSY days)
          // DateCheck already contains 7 days from first date
        } else {
          // DateCheck for week view: 7 days starting from the first date of the date range
          if (busyDateRange[0]) {
            const firstDate = new Date(busyDateRange[0])
            for (let i = 0; i < 7; i++) {
              const date = new Date(firstDate)
              date.setDate(firstDate.getDate() + i)
              const year = date.getFullYear()
              const month = (date.getMonth() + 1).toString().padStart(2, '0')
              const dayNum = date.getDate().toString().padStart(2, '0')
              const dateKey = `${year}-${month}-${dayNum}`
              dateCheckArray.push(dateKey)
            }
          }

          // If Repeated OFF: only update the displayed days (BUSY, AVAILABLE, and PARTIALLY_BUSY)
          modifiedDays.forEach(day => {

            // For BUSY and PARTIALLY_BUSY days, always add busy time slots to BusyTimeObjects
            if ((day.status === BusyStatus.BUSY || day.status === BusyStatus.PARTIALLY_BUSY) && day.hasBusySlots) {
              // Get slots from day object (from daysWithAnalysis) or from busyTimeByDate
              let slotsToUse: TimeSlotWorking[] = []

              // First try to get slots from day object (from daysWithAnalysis)
              if ('slots' in day && day.slots && day.slots.length > 0) {
                slotsToUse = day.slots
              } else {
                // Fallback to busyTimeByDate
                const savedData = busyTimeByDate.get(day.dateKey)
                slotsToUse = savedData?.timeSlots || []
              }

              // Add busy time slots for this BUSY or PARTIALLY_BUSY day
              const busySlotsForDay = slotsToUse.filter(slot => slot.isBusyTime)
              busySlotsForDay.forEach((slot, idx) => {
                const isLastBlock = idx === busySlotsForDay.length - 1
                busyTimeObjects.push({
                  Start: convertMinutesToDateTimeString(slot.start, day.date),
                  End: convertMinutesToDateTimeString(slot.end, day.date),
                  SpIds: spIds,
                  ...(isLastBlock ? { IsLastBlock: true } : {}),
                })
              })
            }
          })
        }

        // // If Repeated OFF: only update the displayed 7 days (BUSY, AVAILABLE, and PARTIALLY_BUSY)
        // const daysToUpdate = daysWithAnalysis.filter(day => {
        //   const isModify = day.status === BusyStatus.BUSY || day.status === BusyStatus.AVAILABLE || day.status === BusyStatus.PARTIALLY_BUSY
        //   return isModify && day.hasBusySlots
        // })

        // daysToUpdate.forEach((day: { date: Date; dateKey: string }) => {
        //   // Add this date to dateCheck array
        //   const year = day.date.getFullYear()
        //   const month = (day.date.getMonth() + 1).toString().padStart(2, '0')
        //   const dayNum = day.date.getDate().toString().padStart(2, '0')
        //   const dateKey = `${year}-${month}-${dayNum}`
        //   dateCheckArray.push(dateKey)

        //   // Get time slots for this specific date from busyTimeByDate
        //   const savedData = busyTimeByDate.get(day.dateKey)
        //   const slotsToUse = savedData?.timeSlots || timeSlots

        //   // Create busy time objects for this date (only busy slots)
        //   slotsToUse.filter(slot => slot.isBusyTime).forEach(slot => {
        //     busyTimeObjects.push({
        //       Start: convertMinutesToDateTimeString(slot.start, day.date),
        //       End: convertMinutesToDateTimeString(slot.end, day.date),
        //       SpIds: spIds
        //     })
        //   })
        // })

        // Calculate NumOfRepeat: if hasRepeatedDays, use numOfRepeated (number of weeks with busy days), otherwise use 1
        const numOfRepeat = hasRepeatedDays ? numOfRepeated : 1

        // Format EndDateInRange: last date in the selected date range (yyyy-MM-dd format)
        const endDateInRange = busyDateRange[1]
          ? (() => {
              const year = busyDateRange[1].getFullYear()
              const month = (busyDateRange[1].getMonth() + 1).toString().padStart(2, '0')
              const day = busyDateRange[1].getDate().toString().padStart(2, '0')
              return `${year}-${month}-${day}`
            })()
          : null

        const payload = isBus
          ? {
            SPIds: spIds,
            DateCheck: dateCheckArray,
            BusyTimeObjects: JSON.stringify(busyTimeObjects),
            IsBusyAllDay: true,
            IsRepeated: hasRepeatedDays,
            RepeatType: 1, // Week
            NumOfRepeat: numOfRepeat,
            EndDateInRange: endDateInRange,
            ClientTimeZone: timezoneOffset
          }
          : {
            WorkerId: userId,
            ApplyToWorkerIds: applyToWorkerIds,
            DateCheck: dateCheckArray.map(dateStr => new Date(dateStr)),
            BusyTimeObjects: JSON.stringify(busyTimeObjects),
            IsBusyAllDay: true,
            IsRepeated: hasRepeatedDays,
            RepeatType: 1, // Week
            NumOfRepeat: numOfRepeat,
            EndDateInRange: endDateInRange,
            ClientTimeZone: timezoneOffset
          }

        const response = isBus
          ? await busyTimeApi.updateBusyTimeForBus(payload)
          : await busyTimeApi.updateBusyTimeForWorker(payload)

        if (response.status === HTTP_RESPONSE_STATUS.SUCCESS) {
          toast.success(t('USERS_PAGE.STEP_UPDATE_USER.BUSY_TIME_UPDATED_SUCCESS'), ToastDefaultConfig())

          // Reload busy time days count after successful update
          loadBusyTimeDays()

          handleClose();
        } else {
          toast.error(response.message || t('USERS_PAGE.STEP_UPDATE_USER.FAILED_TO_UPDATE_BUSY_TIME'), ToastDefaultConfig())
        }
      } else {
        // Handle day view submission - submit all selected dates
        if (!busyDateRange[0]) {
          toast.error(t('USERS_PAGE.STEP_UPDATE_USER.BUSY_DATE_FROM_REQUIRED'), ToastDefaultConfig())
          setIsLoading(false)
          return
        }

        const datesInRange: Date[] = []
        if (busyDateRange[0] && busyDateRange[1]) {
          const startDate = new Date(busyDateRange[0])
          const endDate = new Date(busyDateRange[1])

          if (isRepeated) {
            const currentDate = new Date(startDate)
            while (currentDate <= endDate) {
              datesInRange.push(new Date(currentDate))
              currentDate.setDate(currentDate.getDate() + 1)
            }
          } else {
            datesInRange.push(new Date(startDate))
          }
        } else if (busyDateRange[0]) {
          datesInRange.push(new Date(busyDateRange[0]))
        }

        // Create a temporary Map that includes current timeSlots
        const tempBusyTimeByDate = new Map(busyTimeByDate)
        if (selectedDate && selectedDate[0] && timeSlots.length > 0) {
          // Format dateKey manually to avoid timezone issues
          const year = selectedDate[0].getFullYear()
          const month = (selectedDate[0].getMonth() + 1).toString().padStart(2, '0')
          const day = selectedDate[0].getDate().toString().padStart(2, '0')
          const currentDateKey = `${year}-${month}-${day}`
          
          // Check if current date has repeated ON and is part of a range
          const isCurrentDateRepeated = repeatedByDate.get(currentDateKey) || false
          const storedRange = repeatedRangeByDate.get(currentDateKey)
          
          // Set current date
          tempBusyTimeByDate.set(currentDateKey, {
            timeSlots: timeSlots,
            blockAllDay: blockAllDay
          })
          
          // If repeated is ON and we have a stored range, also add all dates in range to tempBusyTimeByDate
          if (isCurrentDateRepeated && storedRange && busyDateRange[0] && busyDateRange[1]) {
            const [startDate, endDate] = storedRange
            const cursor = new Date(startDate)
            while (cursor <= endDate) {
              const rangeYear = cursor.getFullYear()
              const rangeMonth = (cursor.getMonth() + 1).toString().padStart(2, '0')
              const rangeDay = cursor.getDate().toString().padStart(2, '0')
              const rangeDateKey = `${rangeYear}-${rangeMonth}-${rangeDay}`
              
              // Get data from busyTimeByDate for this date (should already be there from when repeated was enabled)
              const rangeData = busyTimeByDate.get(rangeDateKey)
              if (rangeData && rangeData.timeSlots.length > 0) {
                tempBusyTimeByDate.set(rangeDateKey, {
                  timeSlots: rangeData.timeSlots.map(slot => ({ ...slot })),
                  blockAllDay: rangeData.blockAllDay
                })
              } else if (rangeDateKey === currentDateKey) {
                // If it's the current date, use current timeSlots
                tempBusyTimeByDate.set(rangeDateKey, {
                  timeSlots: timeSlots.map(slot => ({ ...slot })),
                  blockAllDay: blockAllDay
                })
              }
              
              cursor.setDate(cursor.getDate() + 1)
            }
          }
        }

        // Collect busy time objects and dates for all dates that have data in Map
        // Get all dates from busyTimeByDate (not just selectedDate or busyDateRange)
        let allBusyTimeObjects: BusyTimeSPObject[] = []
        let dateCheckArray: string[] = []
        
        // Get all dates that have been modified (from busyTimeByDate)
        // But filter to only include dates that were modified in current session
        let allModifiedDateKeys = Array.from(tempBusyTimeByDate.keys()).filter(dateKey => 
          modifiedDatesInSession.has(dateKey)
        )
        
        // FIX: If user didn't modify anything (no dates in modifiedDatesInSession),
        // we need to send ALL existing busy time to API to preserve them.
        // Otherwise, API will clear all busy time because no BusyTimeObjects are sent.
        if (allModifiedDateKeys.length === 0 && modifiedDatesInSession.size === 0) {
          // User didn't modify anything - send all existing busy time from tempBusyTimeByDate
          allModifiedDateKeys = Array.from(tempBusyTimeByDate.keys()).filter(dateKey => {
            const dateData = tempBusyTimeByDate.get(dateKey)
            // Only include dates that have busy time slots
            return dateData && dateData.timeSlots && dateData.timeSlots.some(slot => slot.isBusyTime)
          })
        }

        // Group repeated dates into contiguous sequences and expand per group
        const repeatedKeys = Array.from(repeatedByDate.entries())
          .filter(([, v]) => v)
          .map(([k]) => k)
          .sort()

        // Check if repeated is enabled
        const hasRepeated = repeatedKeys.length > 0

        if (hasRepeated) {
          // If repeated is enabled in day view, process each repeated date range
          // For each date with repeated ON, find its date range and add all dates in that range
          const processedRepeatedRanges = new Set<string>()
          
          repeatedKeys.forEach(repeatedKey => {
            // Get the stored range for this repeated date
            const storedRange = repeatedRangeByDate.get(repeatedKey)
            
            if (storedRange) {
              const [startDate, endDate] = storedRange
              
              // Use the stored range for repeated dates
              const rangeKey = `${startDate.getTime()}-${endDate.getTime()}`
              if (processedRepeatedRanges.has(rangeKey)) {
                return // Already processed this range
              }
              processedRepeatedRanges.add(rangeKey)
              
              // Template = full slot grid from the repeated anchor day only.
              // Do not merge per-date grids from busyTimeByDate for other days (each weekday can differ from API).
              let templateDate: Date | null = null
              let templateFullSlots: TimeSlotWorking[] = []

              const repeatedData = tempBusyTimeByDate.get(repeatedKey)
              if (repeatedData && repeatedData.timeSlots && repeatedData.timeSlots.length > 0) {
                const [y, m, d] = repeatedKey.split('-').map(Number)
                templateDate = new Date(y, m - 1, d)
                templateFullSlots = repeatedData.timeSlots.map((s) => ({ ...s }))
              } else {
                const firstDateKey = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`
                const firstDateData = tempBusyTimeByDate.get(firstDateKey)
                if (firstDateData && firstDateData.timeSlots && firstDateData.timeSlots.length > 0) {
                  templateDate = startDate
                  templateFullSlots = firstDateData.timeSlots.map((s) => ({ ...s }))
                }
              }
              
              // Process dates in range even if no busy slots (to clear busy time)
              if (templateDate && templateFullSlots.length > 0) {
                const templateBusySlots = templateFullSlots.filter((s: TimeSlotWorking) => s.isBusyTime)
                // Add all dates in the range to DateCheck and create BusyTimeObjects for each date
                // If user didn't modify anything, process all dates in range that have busy time
                // Otherwise, only process dates that were modified in current session
                const shouldProcessAllDates = modifiedDatesInSession.size === 0
                const currentDate = new Date(startDate)
                while (currentDate <= endDate) {
                  const year = currentDate.getFullYear()
                  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
                  const day = currentDate.getDate().toString().padStart(2, '0')
                  const dateKey = `${year}-${month}-${day}`
                  
                  // If user didn't modify anything, process all dates with busy time
                  // Otherwise, only process dates that were modified in current session
                  if (!shouldProcessAllDates && !modifiedDatesInSession.has(dateKey)) {
                    currentDate.setDate(currentDate.getDate() + 1)
                    continue
                  }
                  
                  // If processing all dates, check if this date has busy time
                  if (shouldProcessAllDates) {
                    const dateData = tempBusyTimeByDate.get(dateKey)
                    const hasBusyTime = dateData && dateData.timeSlots && dateData.timeSlots.some(slot => slot.isBusyTime)
                    if (!hasBusyTime) {
                      currentDate.setDate(currentDate.getDate() + 1)
                      continue
                    }
                  }
                  
                  if (!dateCheckArray.includes(dateKey)) {
                    dateCheckArray.push(dateKey)
                  }
                  
                  // Same minute pattern as template day for every date in the repeat range
                  if (templateBusySlots.length > 0) {
                    const objs: BusyTimeSPObject[] = []
                    templateBusySlots.forEach((slot: TimeSlotWorking, idx) => {
                      objs.push({
                        Start: convertMinutesToDateTimeString(slot.start, currentDate),
                        End: convertMinutesToDateTimeString(slot.end, currentDate),
                        SpIds: spIds,
                        ...(idx === templateBusySlots.length - 1 ? { IsLastBlock: true } : {}),
                      })
                    })
                    allBusyTimeObjects.push(...objs)
                  }
                  // If no busy slots, still add to DateCheck but no BusyTimeObjects (to clear busy time)
                  
                  currentDate.setDate(currentDate.getDate() + 1)
                }
              }
            }
          })
        } else {
          // If repeated is disabled, add time slots for ALL dates that have been modified
          // Include dates even if they have no busy slots (to clear busy time)
          allModifiedDateKeys.forEach(dateKey => {
            const savedData = tempBusyTimeByDate.get(dateKey)
            if (!savedData || !savedData.timeSlots || savedData.timeSlots.length === 0) {
              return
            }

            // Parse dateKey to Date object
            const [year, month, day] = dateKey.split('-').map(Number)
            const date = new Date(year, month - 1, day)

            // Add to dateCheckArray even if no busy slots (to clear busy time)
            if (!dateCheckArray.includes(dateKey)) {
              dateCheckArray.push(dateKey)
            }

            // Get time slots for this date
            const slotsToUse = savedData.timeSlots

            // Only add busy time objects if there are busy slots
            if (slotsToUse.some(slot => slot.isBusyTime)) {
              const busyTimeObjectsForDate: BusyTimeSPObject[] = []
              const busySlotsForDay = slotsToUse.filter((slot: TimeSlotWorking) => slot.isBusyTime)
              busySlotsForDay.forEach((slot: TimeSlotWorking, idx) => {
                const isLastBlock = idx === busySlotsForDay.length - 1
                busyTimeObjectsForDate.push({
                  Start: convertMinutesToDateTimeString(slot.start, date),
                  End: convertMinutesToDateTimeString(slot.end, date),
                  SpIds: spIds,
                  ...(isLastBlock ? { IsLastBlock: true } : {}),
                })
              })
              allBusyTimeObjects.push(...busyTimeObjectsForDate)
            }
            // If no busy slots, still add to DateCheck but no BusyTimeObjects (to clear busy time)
          })
        }


        // Add non-repeated selected dates normally (only if repeated is enabled)
        // If repeated is disabled, we already handled it above (only first date)
        if (hasRepeated) {
          // Get all modified dates that are not repeated
          // If user didn't modify anything, get all non-repeated dates with busy time
          const shouldProcessAllDates = modifiedDatesInSession.size === 0
          const allModifiedNonRepeatedDates = shouldProcessAllDates
            ? Array.from(tempBusyTimeByDate.keys()).filter(dateKey => {
                const isDateRepeated = repeatedByDate.get(dateKey) || false
                if (isDateRepeated) return false
                const dateData = tempBusyTimeByDate.get(dateKey)
                return dateData && dateData.timeSlots && dateData.timeSlots.some(slot => slot.isBusyTime)
              })
            : Array.from(modifiedDatesInSession).filter(dateKey => {
                const isDateRepeated = repeatedByDate.get(dateKey) || false
                return !isDateRepeated
              })
          
          allModifiedNonRepeatedDates.forEach((dateKey) => {
            const [y, m, d] = dateKey.split('-')
            const specificDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(d))
            
            // Add to DateCheck even if no busy slots (to clear busy time)
            if (!dateCheckArray.includes(dateKey)) {
              dateCheckArray.push(dateKey)
            }
            
            // Get time slots from tempBusyTimeByDate or selectedTimeSlotsByDate
            const dateData = tempBusyTimeByDate.get(dateKey)
            const selectedSlots = selectedTimeSlotsByDate.get(dateKey)
            
            // Get busy slots
            const busySlots = dateData && dateData.timeSlots && dateData.timeSlots.length > 0
              ? dateData.timeSlots.filter(slot => slot.isBusyTime)
              : (selectedSlots && selectedSlots.length > 0 ? selectedSlots.filter(slot => slot.isBusyTime) : [])
            
            // Only add busy time objects if there are busy slots
            if (busySlots.length > 0) {
              const fullSlotsForDay =
                dateData && dateData.timeSlots && dateData.timeSlots.length > 0
                  ? dateData.timeSlots
                  : selectedSlots && selectedSlots.length > 0
                    ? selectedSlots
                    : busySlots
              const objs: BusyTimeSPObject[] = []
              const busySlotsForDay = fullSlotsForDay.filter((slot: TimeSlotWorking) => slot.isBusyTime)
              busySlotsForDay.forEach((slot: TimeSlotWorking, idx) => {
                objs.push({
                  Start: convertMinutesToDateTimeString(slot.start, specificDate),
                  End: convertMinutesToDateTimeString(slot.end, specificDate),
                  SpIds: spIds,
                  ...(idx === busySlotsForDay.length - 1 ? { IsLastBlock: true } : {}),
                })
              })
              allBusyTimeObjects.push(...objs)
            }
            // If no busy slots, still add to DateCheck but no BusyTimeObjects (to clear busy time)
          })
        }


        // Allow submit if there are dates in DateCheck (even without BusyTimeObjects, to clear busy time)
        // Only show error if no dates are selected and no busy time objects
        if (dateCheckArray.length === 0 && allBusyTimeObjects.length === 0 && busyTimeDays === 0) {
          toast.error(t('USERS_PAGE.STEP_UPDATE_USER.BUSY_TIME_SLOT_REQUIRED'), ToastDefaultConfig())
          setIsLoading(false)
          return
        }

        // Check if all dates have blockAllDay enabled
        let allDatesBlockAllDay = false
        allDatesBlockAllDay = dateCheckArray.every(dateKey => {
          const savedData = tempBusyTimeByDate.get(dateKey)
          return savedData?.blockAllDay === true
        })

        // Format EndDateInRange: last date in the selected date range (yyyy-MM-dd format)
        const endDateInRange = busyDateRange[1]
          ? (() => {
              const year = busyDateRange[1].getFullYear()
              const month = (busyDateRange[1].getMonth() + 1).toString().padStart(2, '0')
              const day = busyDateRange[1].getDate().toString().padStart(2, '0')
              return `${year}-${month}-${day}`
            })()
          : null

        // For day view, use all dates from dateCheckArray (which includes all modified dates)
        // Only use first date if dateCheckArray is empty (fallback)
        const finalDateCheckArray = dateCheckArray.length > 0
          ? dateCheckArray
          : (busyDateRange[0]
              ? (() => {
                  const year = busyDateRange[0].getFullYear()
                  const month = (busyDateRange[0].getMonth() + 1).toString().padStart(2, '0')
                  const day = busyDateRange[0].getDate().toString().padStart(2, '0')
                  return [`${year}-${month}-${day}`]
                })()
              : [])

        // Calculate NumOfRepeat for day view: if has repeated, use total number of days in date range, otherwise use 1
        let numOfRepeatDayView = 1
        if (hasRepeated && busyDateRange[0] && busyDateRange[1]) {
          // Calculate total number of days in the date range
          const startDate = new Date(busyDateRange[0])
          const endDate = new Date(busyDateRange[1])
          const diffTime = endDate.getTime() - startDate.getTime()
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // +1 to include both start and end dates
          numOfRepeatDayView = diffDays
        }

        const payload = isBus
          ? {
            SPIds: spIds,
            DateCheck: finalDateCheckArray,
            BusyTimeObjects: JSON.stringify(allBusyTimeObjects),
            IsBusyAllDay: allDatesBlockAllDay,
            IsRepeated: hasRepeated,
            RepeatType: 0, // Day
            NumOfRepeat: numOfRepeatDayView,
            EndDateInRange: endDateInRange,
            ClientTimeZone: timezoneOffset
          }
          : {
            WorkerId: userId,
            ApplyToWorkerIds: applyToWorkerIds,
            DateCheck: finalDateCheckArray.map(dateStr => new Date(dateStr)),
            BusyTimeObjects: JSON.stringify(allBusyTimeObjects),
            IsBusyAllDay: allDatesBlockAllDay,
            IsRepeated: hasRepeated,
            RepeatType: 0, // Day
            NumOfRepeat: numOfRepeatDayView,
            EndDateInRange: endDateInRange,
            ClientTimeZone: timezoneOffset
          }

        const response = isBus
          ? await busyTimeApi.updateBusyTimeForBus(payload)
          : await busyTimeApi.updateBusyTimeForWorker(payload)

        if (response.status === HTTP_RESPONSE_STATUS.SUCCESS) {
          // Update the actual Map with temp data
          setBusyTimeByDate(tempBusyTimeByDate)
          toast.success(t('USERS_PAGE.STEP_UPDATE_USER.BUSY_TIME_UPDATED_SUCCESS'), ToastDefaultConfig())

          // Reload busy time days count after successful update
          loadBusyTimeDays()

          handleClose();
        } else {
          toast.error(response.message || t('USERS_PAGE.STEP_UPDATE_USER.FAILED_TO_UPDATE_BUSY_TIME'), ToastDefaultConfig())
        }
      }
    } catch (error: any) {
      console.error('Error updating busy time:', error)
      const errorMessage = error.response?.data?.message
        || error.response?.data?.title
        || error.message
        || t('USERS_PAGE.STEP_UPDATE_USER.FAILED_TO_UPDATE_BUSY_TIME')
      toast.error(errorMessage, ToastDefaultConfig())
    } finally {
      setIsLoading(false)
    }
  }

  // Render helpers
  const renderBusyDatesSection = () => (
    <Row className='mt-6'>
      <Col md={12}>
        <Form.Group>
          <Form.Label className='lbl-busy-dates'>
            {t('USERS_PAGE.STEP_UPDATE_USER.BUSY_DATES')}
          </Form.Label>
          <div className='lbl-busy-dates-description'>
            {t('USERS_PAGE.STEP_UPDATE_USER.CONFIGURE_OFF_OR_BUSY_DATE_RANGES')}
          </div>
        </Form.Group>
      </Col>
    </Row>
  )

  const renderDateRangePicker = () => (
    <Row className='date-range-section mt-6'>
      <Col md={12}>
        <Form.Group>
          <Form.Label className='lbl-busy-date-from'>
            {t('USERS_PAGE.STEP_UPDATE_USER.BUSY_DATE_FROM')}
          </Form.Label>
          <div className='date-range-input-wrapper'>
            <div className='calendar-icon'>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2V5" stroke="#193459" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16 2V5" stroke="#193459" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M3.5 9.08984H20.5" stroke="#193459" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="#193459" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M15.6947 13.7002H15.7037" stroke="#193459" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M15.6947 16.7002H15.7037" stroke="#193459" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M11.9955 13.7002H12.0045" stroke="#193459" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M11.9955 16.7002H12.0045" stroke="#193459" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8.29431 13.7002H8.30329" stroke="#193459" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8.29431 16.7002H8.30329" stroke="#193459" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>

            </div>
            <DatePicker
              ref={datePickerRef}
              locale={language === Languages.ar ? ar : en}
              selected={tempDateRange[0]}
              onChange={handleDateRangeChange}
              startDate={tempDateRange[0]}
              endDate={tempDateRange[1]}
              selectsRange
              dateFormat='d MMMM yyyy'
              dayClassName={getDayClassName}
              onMonthChange={(date) => {
                loadBusyTimeForMonth(date.getFullYear(), date.getMonth())
              }}
              customInput={
                <input
                  type='text'
                  className='form-control date-range-input'
                  placeholder={language === Languages.ar ? 'اختر تاريخ البداية - اختر تاريخ النهاية' : 'Select start date - Select end date'}
                  value={formatDateRange(busyDateRange[0], busyDateRange[1])}
                  readOnly
                />
              }
              minDate={new Date()}
              showPopperArrow={false}
              popperClassName='date-range-popper'
              shouldCloseOnSelect={false}
              open={isDatePickerOpen}
              onInputClick={() => setIsDatePickerOpen(true)}
              onCalendarClose={() => setIsDatePickerOpen(false)}
              onClickOutside={() => {
                // Close popup when clicking outside
                setIsDatePickerOpen(false)
              }}
              popperPlacement={
                (language === Languages.ar
                  ? popperPlacement.TOP_END
                  : popperPlacement.TOP_START) as Placement
              }
              calendarContainer={({ children }) => (
                <div className="date-picker-container">
                  {children}
                  <div className="date-picker-footer">
                    <button
                      type="button"
                      className="btn btn-picker-done"
                      onClick={() => {
                        // Check if both from and to dates are selected
                        if (tempDateRange[0] && tempDateRange[1]) {
                          // Apply the selected dates
                          handleDateRangeApply()

                          // Close the date picker popup using ref
                          if (datePickerRef.current) {
                            datePickerRef.current.setOpen(false)
                          }
                        } else {
                          // Show message if dates are not complete
                          toast.warning(
                            t('USERS_PAGE.STEP_UPDATE_USER.PLEASE_SELECT_BOTH_FROM_AND_TO_DATES'),
                            ToastDefaultConfig()
                          )
                        }
                      }}
                    >
                      {t('USERS_PAGE.STEP_UPDATE_USER.DONE')}
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
        </Form.Group>
      </Col>
    </Row>
  )

  const renderWeekView = () => {
    const weekDays = generateWeekDays(busyDateRange[0], busyDateRange[1])

    return (
      <Row className='mt-6'>
        <Col md={12}>
          <Form.Group>
            {/* Row 1: BUSY_DATES label and Day/Week toggle switch */}
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <Form.Label className='mb-0 lbl-busy-dates-xl'>
                {t('USERS_PAGE.STEP_UPDATE_USER.BUSY_DATES')}
              </Form.Label>
              <div className='tab-toggle-switch'>
                <button
                  type='button'
                  className={`tab-toggle-option ${activeTab === 'day' ? 'active' : ''}`}
                  onClick={() => handleTabChange('day')}
                >
                  {t('USERS_PAGE.STEP_UPDATE_USER.DAY')}
                </button>
                <button
                  type='button'
                  className={`tab-toggle-option ${activeTab === 'week' ? 'active' : ''}`}
                  onClick={() => handleTabChange('week')}
                >
                  {t('USERS_PAGE.STEP_UPDATE_USER.WEEK')}
                </button>
                <div className={`tab-toggle-slider ${activeTab === 'week' ? 'week' : 'day'}`}></div>
              </div>
            </div>

            {/* Row 2: Block All Week checkbox and Legend */}
            <div className='d-flex align-items-center justify-content-between mt-4'>
              <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid select-toggle'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={blockAllWeekDisplay}
                  onChange={(e) => {
                    const newValue = e.target.checked
                    setBlockAllWeekDisplay(newValue)

                    // Directly update time slots for all week days
                    const weekDays = generateWeekDays(busyDateRange[0], busyDateRange[1])

                    if (newValue) {
                      // Mark all week days as busy
                      setBusyTimeByDate(prev => {
                        const newMap = new Map(prev)
                        weekDays.forEach(day => {
                          const hasTimeSlots = hasTimeSlotsForDate(day.date)
                          if (hasTimeSlots) {
                            const existingData = newMap.get(day.dateKey)
                            if (existingData && existingData.timeSlots && existingData.timeSlots.length > 0) {
                              const hasWorkingSlots = existingData.timeSlots.some(slot => slot.isAvailable || slot.isBusyTime)
                              if (!hasWorkingSlots) {
                                return
                              }

                              const updatedTimeSlots = existingData.timeSlots.map(slot => {
                                const isOff = !slot.isAvailable && !slot.isBusyTime
                                if (isOff) return slot
                                return {
                                  ...slot,
                                  isBusyTime: true,
                                  isAvailable: false
                                }
                              })
                              newMap.set(day.dateKey, {
                                timeSlots: updatedTimeSlots,
                                blockAllDay: true
                              })
                            }
                          }
                        })
                        return newMap
                      })

                      // Update week day statuses to BUSY
                      setWeekDayStatuses((prev) => {
                        const newStatuses: { [key: string]: BusyStatusType } = { ...prev }
                        weekDays.forEach(day => {
                          const hasTimeSlots = hasTimeSlotsForDate(day.date)
                          if (hasTimeSlots) {
                            const currentStatus = newStatuses[day.dateKey] || BusyStatus.AVAILABLE
                            if (currentStatus !== BusyStatus.OFF) {
                              newStatuses[day.dateKey] = BusyStatus.BUSY
                            }
                          }
                        })
                        return newStatuses
                      })
                    } else {
                      // Mark all week days as available
                      setBusyTimeByDate(prev => {
                        const newMap = new Map(prev)
                        weekDays.forEach(day => {
                          const hasTimeSlots = hasTimeSlotsForDate(day.date)
                          if (hasTimeSlots) {
                            const existingData = newMap.get(day.dateKey)
                            if (existingData && existingData.timeSlots && existingData.timeSlots.length > 0) {
                              const hasWorkingSlots = existingData.timeSlots.some(slot => slot.isAvailable || slot.isBusyTime)
                              if (!hasWorkingSlots) {
                                return
                              }

                              const updatedTimeSlots = existingData.timeSlots.map(slot => {
                                const isOff = !slot.isAvailable && !slot.isBusyTime
                                if (isOff) return slot
                                return {
                                  ...slot,
                                  isBusyTime: false,
                                  isAvailable: true
                                }
                              })
                              newMap.set(day.dateKey, {
                                timeSlots: updatedTimeSlots,
                                blockAllDay: false
                              })
                            }
                          }
                        })
                        return newMap
                      })

                      // Update week day statuses to AVAILABLE
                      setWeekDayStatuses((prev) => {
                        const newStatuses: { [key: string]: BusyStatusType } = { ...prev }
                        weekDays.forEach(day => {
                          const hasTimeSlots = hasTimeSlotsForDate(day.date)
                          if (hasTimeSlots) {
                            const currentStatus = newStatuses[day.dateKey] || BusyStatus.AVAILABLE
                            if (currentStatus !== BusyStatus.OFF) {
                              newStatuses[day.dateKey] = BusyStatus.AVAILABLE
                            }
                          }
                        })
                        return newStatuses
                      })
                    }
                  }}
                />
                <label className='form-check-label'>
                  {t('USERS_PAGE.STEP_UPDATE_USER.BLOCK_ALL_WEEK_AS_BUSY')}
                </label>
              </div>
              <div className='legend-container d-flex align-items-center'>
                <div className='legend-item'>
                  <div className='legend-dot available'></div>
                  <span>{t('USERS_PAGE.STEP_UPDATE_USER.AVAILABLE')}</span>
                </div>
                <div className='legend-item'>
                  <div className='legend-dot busy'></div>
                  <span>{t('USERS_PAGE.STEP_UPDATE_USER.BUSY')}</span>
                </div>
                <div className='legend-item'>
                  <div className='legend-dot partially_busy'></div>
                  <span>{t('USERS_PAGE.STEP_UPDATE_USER.PARTIALLY_BUSY')}</span>
                </div>
                <div className='legend-item'>
                  <div className='legend-dot off'></div>
                  <span>{t('USERS_PAGE.STEP_UPDATE_USER.OFF')}</span>
                </div>
              </div>
            </div>

            {/* Week days grid */}
            <div className={`week-days-grid mt-5 ${weekDays.length % 3 === 1 ? 'single-last-item' : weekDays.length % 3 === 2 ? 'double-last-item' : ''}`}>
              {isLoadingTimeSlots ? (
                <>
                  {/* Render skeleton week day cards with loading icons */}
                  {Array.from({ length: 7 }).map((_, index) => (
                    <div
                      key={index}
                      className='week-day-card available d-flex align-items-center justify-content-center'
                      style={{ cursor: 'default', opacity: 0.6 }}
                    >
                      <span className='spinner-border spinner-border-sm text-primary' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {weekDays.map((day) => {
                    // Check if we have cached data for this date
                    const hasCachedData = hasTimeSlotsForDate(day.date)

                    // If we have cached data, use it to determine status
                    let status = weekDayStatuses[day.dateKey]
                    if (!status) {
                      if (hasCachedData) {
                        // Check the actual time slot states for this date
                        const savedData = busyTimeByDate.get(day.dateKey)
                        if (savedData && savedData.timeSlots && savedData.timeSlots.length > 0) {
                          const allBusy = savedData.timeSlots.every(slot => slot.isBusyTime)
                          const allOff = savedData.timeSlots.every(slot => !slot.isAvailable && !slot.isBusyTime)
                          const allAvailable = savedData.timeSlots.every(slot => slot.isAvailable && !slot.isBusyTime)
                          const hasBusy = savedData.timeSlots.some(slot => slot.isBusyTime)
                          const hasAvailable = savedData.timeSlots.some(slot => slot.isAvailable && !slot.isBusyTime)

                          if (allBusy) {
                            status = BusyStatus.BUSY
                          } else if (allOff) {
                            status = BusyStatus.OFF
                          } else if (allAvailable) {
                            status = BusyStatus.AVAILABLE
                          } else if (hasBusy && hasAvailable) {
                            status = BusyStatus.PARTIALLY_BUSY
                          } else {
                            status = BusyStatus.AVAILABLE // Mixed state
                          }
                        } else {
                          // No time slots means OFF status
                          status = BusyStatus.OFF
                        }
                      } else {
                        // No cached data - check if we have empty time slots (no working hours)
                        const savedData = busyTimeByDate.get(day.dateKey)
                        if (savedData && savedData.timeSlots && savedData.timeSlots.length === 0) {
                          // Empty time slots means no working hours - OFF status
                          status = BusyStatus.OFF
                        } else {
                          // No cached data yet, assume available until we know otherwise
                          status = BusyStatus.AVAILABLE
                        }
                      }
                    }

                    const isDisabled = status === BusyStatus.OFF
                    return (
                      <div
                        key={day.dateKey}
                        className={`week-day-card ${status} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => {
                          if (isDisabled) {
                            // If disabled (no time slots), do nothing
                            return
                          }

                          // Close the date picker popup
                          setIsDatePickerOpen(false)

                          // Toggle day status in week view and update all time slots
                          const existingData = busyTimeByDate.get(day.dateKey)
                          if (!existingData || !existingData.timeSlots || existingData.timeSlots.length === 0) {
                            return // No time slots to update
                          }

                          const currentStatus = weekDayStatuses[day.dateKey] || BusyStatus.AVAILABLE
                          let newStatus: BusyStatusType
                          let updatedTimeSlots: TimeSlotWorking[]

                          // Determine new status and update time slots accordingly
                          if (currentStatus === BusyStatus.AVAILABLE) {
                            // Change from AVAILABLE to BUSY - set all slots to busy
                            newStatus = BusyStatus.BUSY
                            updatedTimeSlots = existingData.timeSlots.map(slot => {
                              const isOff = !slot.isAvailable && !slot.isBusyTime
                              if (isOff) return slot
                              return {
                                ...slot,
                                isBusyTime: true,
                                isAvailable: false
                              }
                            })
                          } else if (currentStatus === BusyStatus.BUSY || currentStatus === BusyStatus.PARTIALLY_BUSY) {
                            // Change from BUSY/PARTIALLY_BUSY to AVAILABLE - set all slots to available
                            newStatus = BusyStatus.AVAILABLE
                            updatedTimeSlots = existingData.timeSlots.map(slot => {
                              const isOff = !slot.isAvailable && !slot.isBusyTime
                              if (isOff) return slot
                              return {
                                ...slot,
                                isBusyTime: false,
                                isAvailable: true
                              }
                            })
                          } else {
                            // Default case
                            newStatus = BusyStatus.BUSY
                            updatedTimeSlots = existingData.timeSlots.map(slot => {
                              const isOff = !slot.isAvailable && !slot.isBusyTime
                              if (isOff) return slot
                              return {
                                ...slot,
                                isBusyTime: true,
                                isAvailable: false
                              }
                            })
                          }

                          // Update busyTimeByDate with new time slots
                          setBusyTimeByDate(prev => {
                            const newMap = new Map(prev)
                            newMap.set(day.dateKey, {
                              timeSlots: updatedTimeSlots,
                              blockAllDay: newStatus === BusyStatus.BUSY
                            })
                            return newMap
                          })

                          // Update week day status
                          setWeekDayStatuses((prev) => {

                            const newStatuses = { ...prev, [day.dateKey]: newStatus }

                            // Check if all week days are busy for display toggle
                            const weekDays = generateWeekDays(busyDateRange[0], busyDateRange[1])
                            const allDaysBusy = weekDays.every(weekDay => {
                              const dayStatus = newStatuses[weekDay.dateKey] || BusyStatus.AVAILABLE
                              return dayStatus === BusyStatus.BUSY
                            })
                            setBlockAllWeekDisplay(allDaysBusy)

                            return newStatuses
                          })
                        }}
                        style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                      >
                        <div className='week-day-dot'></div>
                        <div className='week-day-info'>
                          <div className='week-day-name'>{day.dayName}</div>
                          <div className='week-day-date'>{day.dayNumber} {day.monthName}</div>
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          </Form.Group>
        </Col>
      </Row>
    )
  }


  const renderRepeated = () => {
    const currentDateKey = selectedDate && selectedDate[0] ?
      `${selectedDate[0].getFullYear()}-${(selectedDate[0].getMonth() + 1).toString().padStart(2, '0')}-${selectedDate[0].getDate().toString().padStart(2, '0')}` :
      null

    const currentDateRepeated = currentDateKey ? repeatedByDate.get(currentDateKey) || false : false

    return (
      <Row className='mt-6'>
        <Col md={12}>
          <div className='d-flex align-items-center'>
            <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid select-toggle'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={currentDateRepeated}
                disabled={
                  activeTab === 'day' &&
                  (timeSlots.length === 0 || !timeSlots.some(slot => slot.isAvailable || slot.isBusyTime))
                }
                onChange={(e) => {
                  if (currentDateKey) {
                    handleRepeatedToggle(currentDateKey, e.target.checked)

                    // If in week tab, update weekDayStatuses and repeatedByDate for all busy days
                    if (activeTab === 'week') {
                      const weekDays = generateWeekDays(busyDateRange[0], busyDateRange[1])
                      const daysToApply = weekDays.filter(day => {
                        const status = weekDayStatuses[day.dateKey] || BusyStatus.AVAILABLE || BusyStatus.PARTIALLY_BUSY
                        return status === BusyStatus.BUSY || status === BusyStatus.AVAILABLE || status === BusyStatus.PARTIALLY_BUSY
                      })

                      // Update repeatedByDate for BUSY and AVAILABLE days
                      daysToApply.forEach(day => {
                        handleRepeatedToggle(day.dateKey, e.target.checked)
                      })
                    }

                    // In day tab: when a range is selected, propagate Repeated toggle across the whole range
                    if (activeTab === 'day' && busyDateRange[0] && busyDateRange[1]) {
                      const startDate = new Date(busyDateRange[0])
                      const endDate = new Date(busyDateRange[1])
                      
                      if (e.target.checked) {
                        // When enabling repeated, copy current time slots to all dates in range
                        // Get current date key from selectedDate
                        const currentDateKeyForRepeated = selectedDate && selectedDate[0] ?
                          `${selectedDate[0].getFullYear()}-${(selectedDate[0].getMonth() + 1).toString().padStart(2, '0')}-${selectedDate[0].getDate().toString().padStart(2, '0')}` :
                          null
                        const currentData = currentDateKeyForRepeated ? busyTimeByDate.get(currentDateKeyForRepeated) : null
                        
                        // Also check timeSlots state as fallback
                        const dataToUse = currentData && currentData.timeSlots.length > 0 ? currentData : 
                          (timeSlots.length > 0 ? { timeSlots: timeSlots, blockAllDay: blockAllDay } : null)
                        
                        if (dataToUse && dataToUse.timeSlots.length > 0) {
                          // Copy time slots to all dates in range
                          setBusyTimeByDate(prev => {
                            const newMap = new Map(prev)
                            const cursor = new Date(startDate)
                            while (cursor <= endDate) {
                              const year = cursor.getFullYear()
                              const month = (cursor.getMonth() + 1).toString().padStart(2, '0')
                              const day = cursor.getDate().toString().padStart(2, '0')
                              const key = `${year}-${month}-${day}`
                              
                              // Mark all dates in range as modified when repeated is enabled
                              setModifiedDatesInSession(prev => new Set(prev).add(key))
                              
                              // Per-day slot geometry (start/end) must come from API for that date when available;
                              // only copy busy/available flags from the template day.
                              const existingForKey = newMap.get(key)
                              const templateSlots = dataToUse.timeSlots
                              const apiDaySlots = existingForKey?.timeSlots

                              let mergedTimeSlots: TimeSlotWorking[]
                              if (apiDaySlots && apiDaySlots.length > 0) {
                                // Geometry (start/end) always from get-time-slot-working-day for this date.
                                if (apiDaySlots.length === templateSlots.length) {
                                  mergedTimeSlots = apiDaySlots.map((apiSlot, i) => ({
                                    ...apiSlot,
                                    isBusyTime: templateSlots[i].isBusyTime,
                                    isAvailable: templateSlots[i].isAvailable,
                                  }))
                                } else {
                                  mergedTimeSlots = apiDaySlots.map((apiSlot, i) =>
                                    i < templateSlots.length
                                      ? {
                                          ...apiSlot,
                                          isBusyTime: templateSlots[i].isBusyTime,
                                          isAvailable: templateSlots[i].isAvailable,
                                        }
                                      : { ...apiSlot }
                                  )
                                }
                              } else {
                                mergedTimeSlots = templateSlots.map(slot => ({ ...slot }))
                              }

                              newMap.set(key, {
                                timeSlots: mergedTimeSlots,
                                blockAllDay: dataToUse.blockAllDay,
                              })
                              
                              handleRepeatedToggle(key, true)
                              // Store the range for this date when repeated is enabled
                              setRepeatedRangeByDate(prevRange => {
                                const newRangeMap = new Map(prevRange)
                                newRangeMap.set(key, [startDate, endDate])
                                return newRangeMap
                              })
                              
                              cursor.setDate(cursor.getDate() + 1)
                            }
                            return newMap
                          })
                        } else {
                          // If no current data, just set repeated flags and mark dates as modified
                          const cursor = new Date(startDate)
                          while (cursor <= endDate) {
                            const year = cursor.getFullYear()
                            const month = (cursor.getMonth() + 1).toString().padStart(2, '0')
                            const day = cursor.getDate().toString().padStart(2, '0')
                            const key = `${year}-${month}-${day}`
                            
                            // Mark all dates in range as modified when repeated is enabled
                            setModifiedDatesInSession(prev => new Set(prev).add(key))
                            
                            handleRepeatedToggle(key, true)
                            // Store the range for this date when repeated is enabled
                            setRepeatedRangeByDate(prevRange => {
                              const newRangeMap = new Map(prevRange)
                              newRangeMap.set(key, [startDate, endDate])
                              return newRangeMap
                            })
                            cursor.setDate(cursor.getDate() + 1)
                          }
                        }
                      } else {
                        // When disabling repeated, just remove the flags
                        const cursor = new Date(startDate)
                        while (cursor <= endDate) {
                          const year = cursor.getFullYear()
                          const month = (cursor.getMonth() + 1).toString().padStart(2, '0')
                          const day = cursor.getDate().toString().padStart(2, '0')
                          const key = `${year}-${month}-${day}`
                          handleRepeatedToggle(key, false)
                          setRepeatedRangeByDate(prevRange => {
                            const newRangeMap = new Map(prevRange)
                            newRangeMap.delete(key)
                            return newRangeMap
                          })
                          cursor.setDate(cursor.getDate() + 1)
                        }
                      }
                    }
                  }
                }}
              />
              <label className='form-check-label'>
                {t('USERS_PAGE.STEP_UPDATE_USER.REPEATED')}
              </label>
            </div>
          </div>
        </Col>
      </Row>
    )
  }

  const renderBusyDatesConfiguration = () => (
    <Row className='mt-6'>
      <Col md={12}>
        <Form.Group>
          {/* Row 1: BUSY_DATES label and Day/Week toggle switch */}
          <div className='d-flex align-items-center justify-content-between mb-3'>
            <Form.Label className='mb-0 lbl-busy-dates-xl'>
              {t('USERS_PAGE.STEP_UPDATE_USER.BUSY_DATES')}
            </Form.Label>
            <div className='tab-toggle-switch'>
              <button
                type='button'
                className={`tab-toggle-option ${activeTab === 'day' ? 'active' : ''}`}
                onClick={() => handleTabChange('day')}
              >
                {t('USERS_PAGE.STEP_UPDATE_USER.DAY')}
              </button>
              <button
                type='button'
                className={`tab-toggle-option ${activeTab === 'week' ? 'active' : ''}`}
                onClick={() => handleTabChange('week')}
              >
                {t('USERS_PAGE.STEP_UPDATE_USER.WEEK')}
              </button>
              <div className={`tab-toggle-slider ${activeTab === 'week' ? 'week' : 'day'}`}></div>
            </div>
          </div>

          {/* Row 2: Block All Day checkbox and Legend */}
          <div className='d-flex align-items-center justify-content-between mt-4'>
            <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid select-toggle'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={blockAllDayDisplay}
                disabled={timeSlots.length === 0 || !timeSlots.some(slot => slot.isAvailable || slot.isBusyTime)}
                onChange={(e) => {
                  const newValue = e.target.checked
                  setBlockAllDayDisplay(newValue)
                  setBlockAllDay(newValue)

                  // Directly update time slots
                  if (newValue) {
                    // Mark all time slots as busy
                    setTimeSlots(prevSlots =>
                      prevSlots.map(slot => ({
                        ...slot,
                        // Preserve OFF slots
                        ...( !slot.isAvailable && !slot.isBusyTime
                          ? {}
                          : { isBusyTime: true, isAvailable: false }
                        )
                      }))
                    )
                  } else {
                    // Mark all time slots as available
                    setTimeSlots(prevSlots =>
                      prevSlots.map(slot => ({
                        ...slot,
                        // Preserve OFF slots
                        ...( !slot.isAvailable && !slot.isBusyTime
                          ? {}
                          : { isBusyTime: false, isAvailable: true }
                        )
                      }))
                    )
                  }

                  // Save to Map for current date
                  if (selectedDate && selectedDate[0]) {
                    const year = selectedDate[0].getFullYear()
                    const month = (selectedDate[0].getMonth() + 1).toString().padStart(2, '0')
                    const day = selectedDate[0].getDate().toString().padStart(2, '0')
                    const dateKey = `${year}-${month}-${day}`

                    // Mark this date as modified in current session
                    setModifiedDatesInSession(prev => new Set(prev).add(dateKey))

                    setBusyTimeByDate(prev => {
                      const newMap = new Map(prev)
                      const existingData = newMap.get(dateKey) || { timeSlots: [], blockAllDay: false }

                      // Update time slots in the map
                      const updatedTimeSlots = existingData.timeSlots.map(slot => {
                        const isOff = !slot.isAvailable && !slot.isBusyTime
                        if (isOff) return slot
                        return {
                          ...slot,
                          isBusyTime: newValue,
                          isAvailable: !newValue
                        }
                      })

                      const workingSlots = updatedTimeSlots.filter(slot => slot.isAvailable || slot.isBusyTime)
                      const allWorkingSlotsBusy = workingSlots.length > 0 && workingSlots.every(slot => slot.isBusyTime)

                      newMap.set(dateKey, {
                        timeSlots: updatedTimeSlots.length > 0 ? updatedTimeSlots : existingData.timeSlots,
                        blockAllDay: allWorkingSlotsBusy
                      })
                      return newMap
                    })
                  }
                }}
              />
              <label className='form-check-label'>
                {t('USERS_PAGE.STEP_UPDATE_USER.BLOCK_ALL_DAY_AS_BUSY')}
              </label>
            </div>
            <div className='legend-container d-flex align-items-center'>
              <div className='legend-item'>
                <img src='/assets/images/icons/time-slot-available.svg' alt='Available' />
                <span>{t('USERS_PAGE.STEP_UPDATE_USER.AVAILABLE')}</span>
              </div>
              <div className='legend-item'>
                <img src='/assets/images/icons/time-slot-busy.svg' alt='Busy' />
                <span>{t('USERS_PAGE.STEP_UPDATE_USER.BUSY')}</span>
              </div>
            </div>
          </div>

          {/* Time slot grid - 4 slots per row */}
          <div className='time-slot-grid mt-5'>
            {isLoadingTimeSlots ? (
              <>
                {/* Render skeleton time slots with loading icons - 3 rows of 4 slots */}
                {Array.from({ length: 3 }).map((_, rowIndex) => (
                  <div key={rowIndex} className='time-slot-row'>
                    {Array.from({ length: 4 }).map((_, slotIndex) => (
                      <div
                        key={slotIndex}
                        className='time-slot-bus available d-flex align-items-center justify-content-center'
                        style={{ cursor: 'default', opacity: 0.6 }}
                      >
                        <span className='spinner-border spinner-border-sm text-primary' role='status'>
                          <span className='visually-hidden'>Loading...</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </>
            ) : timeSlots.length === 0 && hasEmptyTimeSlots ? (
              <div className='d-flex justify-content-center align-items-center py-5'>
                <div className='text-center text-muted fs-6 fw-semibold'>
                  {t('USERS_PAGE.STEP_UPDATE_USER.NO_WORKING_HOUR_AVAILABLE')}
                </div>
              </div>
            ) : (
              <>
                {/* Render time slots in rows of 4 */}
                {Array.from({ length: Math.ceil(timeSlots.length / 4) }).map((_, rowIndex) => (
                  <div key={rowIndex} className='time-slot-row'>
                    {timeSlots.slice(rowIndex * 4, (rowIndex + 1) * 4).map((slot) => {
                      const isOff = !slot.isAvailable && !slot.isBusyTime
                      const stateClass = slot.isBusyTime ? 'busy' : isOff ? 'off' : 'available'

                      return (
                        <div
                          key={slot.start + slot.end}
                          className={`time-slot-bus ${stateClass}`}
                          onClick={() => {
                            if (isOff) return
                            handleTimeSlotToggle(slot.start, slot.end)
                          }}
                          style={{ cursor: isOff ? 'not-allowed' : 'pointer' }}
                        >
                          <div className='time-slot-icon'>
                            {slot.isBusyTime ? (
                              <img src='/assets/images/icons/time-slot-busy.svg' alt='Busy' />
                            ) : isOff ? (
                              <img src='/assets/images/icons/time-slot-off.svg' alt='Off' />
                            ) : (
                              <img src='/assets/images/icons/time-slot-available.svg' alt='Available' />
                            )}
                          </div>
                          <div className='time-slot-time'>
                            {convertMinutesToTime(slot.start)} - {convertMinutesToTime(slot.end)}
                            {isNextDayTimeSlot(slot.start, slot.end) && (
                              <div className='time-slot-next-day'>
                                {t('NEXT_DAY')}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ))}
              </>
            )}
          </div>
        </Form.Group>
      </Col>
    </Row>
  )

  return (
    <Modal
      show={!!userId}
      onHide={() => {
        resetForm()
        onHide?.()
      }}
      backdrop='static'
      keyboard={false}
      size='lg'
      className={`set-busytime-modal ${rtlMode ? 'set-busytime-modal--rtl' : ''}`}
    >
      <Modal.Header className='bg-warning'>
        <button
          type='button'
          className='btn-close'
          onClick={() => {
            resetForm()
            if (onCloseButtonClick) {
              onCloseButtonClick()
            } else {
              onHide?.()
            }
          }}
          aria-label='Close'
        ></button>
        <Modal.Title className='fw-boldest'>
          {t('USERS_PAGE.STEP_UPDATE_USER.TITLE_BUSYTIME')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Apply to field */}
          <Row className='form-bus'>
            <Col md={6}>
              <Form.Group>
                <Form.Label className='form-label'>
                  {t('USERS_PAGE.STEP_UPDATE_USER.APPLY_TO')}
                </Form.Label>
                <InputGroup className='filter-calender'>
                  {isWorker ? (
                    <MultiSelect
                      options={workerOptions
                        .filter((worker) => worker.id && (rtlMode ? worker.fullNameAr : worker.fullNameEn))
                        .map((worker) => ({
                          label: (rtlMode ? worker.fullNameAr : worker.fullNameEn) || '',
                          value: worker.id || ''
                        }))}
                      value={selectedWorkers
                        .filter((worker) => worker.id && (rtlMode ? worker.fullNameAr : worker.fullNameEn))
                        .map((worker) => ({
                          label: (rtlMode ? worker.fullNameAr : worker.fullNameEn) || '',
                          value: worker.id || ''
                        }))}
                      onChange={handleWorkerChange}
                      labelledBy={'Select Workers'}
                      className='form-multi-select form-multi-select-mobile apply-to-multi-select'
                      overrideStrings={MULTISELECT_STRINGS}
                    />
                  ) : (
                    <MultiSelect
                      options={busOptions.map((bus) => ({ label: rtlMode ? bus.fullNameAr : bus.fullNameEn, value: bus.id }))}
                      value={selectedBuses.map((bus) => ({ label: rtlMode ? bus.fullNameAr : bus.fullNameEn, value: bus.id }))}
                      onChange={handleBusChange}
                      labelledBy={'Select Buses'}
                      className='form-multi-select form-multi-select-mobile apply-to-multi-select'
                      overrideStrings={MULTISELECT_STRINGS}
                    />
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className='form-label'>
                  {t('USERS_PAGE.STEP_UPDATE_USER.BUSY_DAYS')}
                </Form.Label>
                <div className='position-relative'>
                  <Form.Control
                    type='text'
                    value={isLoadingBusyTimeDays ? '' : (busyTimeDays?.toString() || '')}
                    disabled={true}
                    readOnly
                    className='form-busy-days'
                  />
                  {isLoadingBusyTimeDays && (
                    <div
                      className='position-absolute top-50 translate-middle-y'
                      style={rtlMode ? { right: '12px' } : { left: '12px' }}
                    >
                      <span className='spinner-border spinner-border-sm text-primary' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                      </span>
                    </div>
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>

          {renderBusyDatesSection()}
          {renderDateRangePicker()}
          {activeTab === 'week' ? renderWeekView() : renderBusyDatesConfiguration()}
          {renderRepeated()}
        </Form>
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-between'>
        <div className='text-start'>
          <button
            className='btn btn-busy-close'
            onClick={() => {
              resetForm()
              if (onCloseButtonClick) {
                onCloseButtonClick()
              } else {
                onHide?.()
              }
            }}
            disabled={isLoading}
          >
            {t('USERS_PAGE.CLOSE')}
          </button>
        </div>
        <div className='text-end' style={{ display: 'flex', gap: '16px' }}>
          <button
            className='btn btn-busy-reset'
            onClick={handleResetAllBusyTime}
            disabled={isLoading}
          >
            {t('USERS_PAGE.STEP_UPDATE_USER.RESET_ALL_BUSY_TIME')}
          </button>
          <button className='btn btn-busy-update' onClick={handleSubmit} disabled={isLoading}>
            {isLoading && (
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              ></span>
            )}
            {t('USERS_PAGE.UPDATE')}
          </button>
        </div>
      </Modal.Footer>

      {/* Confirm Reset Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('USERS_PAGE.STEP_UPDATE_USER.CONFIRM_RESET')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('USERS_PAGE.STEP_UPDATE_USER.CONFIRM_RESET_MESSAGE')}</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            type='button'
            className='btn btn-secondary'
            onClick={() => setShowConfirmModal(false)}
            disabled={isLoading}
          >
            {t('USERS_PAGE.STEP_UPDATE_USER.CLOSE')}
          </button>
          <button
            type='button'
            className='btn btn-danger'
            onClick={confirmResetAllBusyTime}
            disabled={isLoading}
          >
            {isLoading && (
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              ></span>
            )}
            {t('USERS_PAGE.STEP_UPDATE_USER.RESET_ALL')}
          </button>
        </Modal.Footer>
      </Modal>
    </Modal>
  )
}

